import moment from 'moment'
import LocalizedStrings, {
  LocalizedStrings as LocalizedStringsGenericType,
} from 'react-localization'

/* ------------- Types ------------- */
export type StringsType = typeof enBaseStrings

export type LocalizedStringsType = LocalizedStringsGenericType<StringsType>

type SupportedLanguagesType = 'en'

/* ------------- Strings ------------- */
/* eslint sort-keys-fix/sort-keys-fix: ["error", "asc", { caseSensitive: false, natural: false }] */
const enBaseStrings = {
  accept: 'Accept',
  acceptBriefing: 'Accept Briefing',
  acceptedValues: 'Accepted Values',
  acceptInvite: 'Accept Invite',
  acceptInviteLogOutFirst: 'Log out from profile to accept the invite',
  acceptPermitTransferAcknowledge:
    'By signing this transfer you acknowledge your responsibility as the new Permit Holder.',
  acceptTransfer: 'Accept Transfer',
  accessSiteassist: 'Access SiteAssist',
  accessToken: 'Access Token',
  accountOwner: 'Account Owner',
  accountViewer: 'Account Viewer',
  accuracy: 'Accuracy',
  acknowledge: 'Acknowledge',
  acknowledged: 'Acknowledged',
  acknowledgeDisclaimer: 'Please check this box to acknowledge the disclaimer',
  acknowledgeDocument: 'Acknowledge Document',
  acknowledgeQuestionPrompt: 'Please review and acknowledge this document',
  acrossPermits: (count: number) =>
    `Across ${count} ${count === 1 ? 'permit' : 'permits'}`,
  action: 'Action',
  actionBy: (action: string, user: boolean) => `${action}${user ? ' by' : ''}`,
  actionRequired: 'Action Required',
  actions: 'Actions',
  actionsInProgress: 'Actions in Progress',
  actionType: 'Action Type',
  activate: 'Activate',
  activateActionMessage: 'You must finish editing to activate this action',
  activatedBy: 'Activated by',
  activatedChecklistVersion: 'Activated Checklist Version',
  activatedOrganisationMember: 'Activated Organisation Member',
  activatedPermitTemplateVersion: 'Permit Version Activated',
  activatedProjectMember: 'Activated Project Member',
  activateUser: 'Activate User',
  activateVersion: 'Activate Version',
  activateVersionText:
    'Making this active will make it the current permit template version.',
  active: 'Active',
  activeDate: 'Active Date',
  activePermits: 'Active Permits',
  activePermitsTooltip:
    'Active Permits are defined with a status of  Awaiting Approval, Pre-start, Open, Extension Requested, and Post Permit Checks',
  activeTasks: 'Active Tasks',
  activeZonesAndPoints: 'Active Zones and Points',
  activity: 'Activity',
  activityTimeline: 'Activity Timeline',
  add: 'Add',
  addAdditionalInfo: 'Add additional info',
  addAdminToProject: 'Add Admin to Project',
  addALevel: 'Add a level',
  addALocation: 'Add a location',
  addAnAttachment: 'add an attachment',
  addAnotherEllipsis: 'Add Another...',
  addAnotherUser: 'Add Another User',
  addAnswer: 'Add Answer',
  addAttachment: 'Add Attachment',
  addAttachments: 'Add Attachments',
  addChecklist: 'Add Checklist',
  addChecklistsToProject: 'Add Checklists To Project',
  addComment: 'Add Comment',
  addCompany: 'Add Company',
  addCompliance: 'Add Compliance',
  addComplianceRequirement: 'Add Requirement',
  addCondition: 'Add Condition',
  addDiagram: 'Add Diagram',
  addDocument: 'Add Document',
  addDocuments: 'Add Documents',
  addDocumentTemplateToProject: 'Add Document Template To Project',
  addDomain: 'Add Domain',
  added: 'Added',
  addedChecklistTemplateToProject: 'Checklist Template Added to Project',
  addedPermitTemplate: 'Added Permit Template to Project',
  addedProperties: 'Added Properties',
  addedUserToAuthGroup: 'Added User to Auth Group',
  addedUserToOrganisation: 'Added User to Organisation',
  addedUserToProject: 'Added User to Project',
  addEvidence: 'Add evidence',
  addExistingSiteAssistUser: 'Existing SiteAssist User',
  addExternalUser: 'Add External User',
  addFileOrFolder: 'Add File or Folder',
  addFileOrFolderDescription: 'Create a new folder or upload a file',
  addFilter: 'Add Filter',
  addGroupMembers: 'Add users to the Group',
  addGroupMembersFromProject:
    'Add the members of this group from your project team',
  addingParticipants: 'Adding Participants',
  addInspection: 'Add Inspection',
  addInspectionsToProject: 'Add Inspections To Project',
  addInspectionTemplate: 'Add Inspection Template',
  addItem: 'Add Item',
  addItems: 'Add Items',
  additionalInfo: 'Additional Info',
  AdditionalInformation: 'Additional Information',
  additionalTextForAnswer: (answer?: string) =>
    `Additional Text ${answer ? `for answer: ${answer}` : '...'}`,
  addLayer: 'Add Layer',
  addLink: 'Add Link',
  addLocation: 'Add Location',
  addManually: 'Add Manually',
  addMembers: 'Add Members',
  addNotification: 'Add Notification',
  addOption: 'Add Option',
  addOrganisationLink: 'Add Link to Organisation',
  addOrRemoveTemplates: 'Add or Remove Permit Templates',
  addParticipant: 'Add Participant',
  addParticipants: 'Add Participants',
  addParticipantsAction: 'add participants',
  addParticipantsAndInformation: 'Add Participants & Information',
  addParticipantsAndInformationDescription:
    'Please add the participants below that you would like to brief. You may be required to capture your current location, and/or add a photo to continue.',
  addParticipantsDescription: 'Add additional participants to the permit',
  addParticipantsManuallyDescription: `You will collect the Participant signatures by tapping Done`,
  addParticipantsToRoleDescription:
    'If the user is already on SiteAssist you can search for them here. If not they can be added separately via their name and Company',
  addPermission: 'Add Permission',
  addPermit: 'Add Permit',
  addPhotos: 'Add Photos',
  addProject: 'Add Project',
  addProperty: 'Add Property',
  addQuery: 'Add Query',
  addRegisterItem: 'Add Register Item',
  addRegisterItems: 'Add Register Items',
  addResponse: 'Add Response',
  address: 'Address',
  addressChanges: 'Address Changes',
  addressedBy: 'Addressed By',
  addressedChange: 'addressed a change to this permit',
  addRole: 'Add Role',
  addRule: 'Add Rule',
  addSignature: 'Add Signature',
  addTaskTemplatesToProject: 'Add Task Templates To Project',
  addTemplatesToProject: 'Add Templates To Project',
  addToProject: 'Add to Project',
  addToTemplate: 'Add to Template',
  addTrigger: 'Add Trigger',
  addUser: 'Add User',
  addUsers: 'Add Users',
  addUserWithoutSaAccount: 'Person Without SiteAssist Account',
  addViaQrCode: 'Add Via QR Code',
  addWarning: 'Add Warning',
  adjustPosition: 'Adjust position',
  admin: 'Admin',
  afterPermitHolder: 'After Permit Holder',
  alert: 'Alert',
  all: 'All',
  allChecklists: 'All Checklists',
  AllChecklists: 'All Checklists',
  allInspections: 'All Inspections',
  allItems: 'All items',
  allLocations: 'All Locations',
  allObjects: 'All Objects',
  allowAdditionalRecipients:
    'Allow the author to add recipients before submitting',
  allowApprovalDrafts: 'Allow Approval Drafts',
  allowBypassAppraisal: 'Allow Bypass Appraisal',
  allowDefaultsToBeOverriden: 'Allow defaults to be overridden',
  allowedMediaTypes: 'Allowed media types',
  allowedProperties: 'Allowed Properties',
  allowedPropertiesHint:
    'When Create Items permission is also selected, Update Items applies to all properties',
  allowExtensionBeyondMaxDuration:
    'Allow extension beyond request max duration',
  allowMultipleLinkedRegisterItems: 'Allow multiple linked register items',
  allowOverride: 'Allow Override',
  allowSelfApproval: 'Allow Self Approval',
  allowSelfApprovalChecklistTooltip:
    'Allow the checklist initiator to approve their own checklist.',
  allPermits: 'All Permits',
  allPriorities: 'All Priorities',
  allProjects: 'All Projects',
  allProperties: 'All Properties',
  allRegisters: 'All Registers',
  allRequirementsAssigned: 'All compliance requirements are already assigned.',
  allStatuses: 'All Statuses',
  allTags: 'All Tags',
  allTasks: 'All Tasks',
  allTemplatesAlreadyAdded:
    'All templates have already been added to this project',
  allTime: 'All Time',
  allUsers: 'All Users',
  alreadyHaveAnAccount: 'Already have an account?',
  alreadyPartOfCompany:
    'You are already part of a registered company in SiteAssist.',
  alreadyUpToDate: 'You are already up to date',
  alsoOnSite: 'Also on site',
  analytics: 'Analytics',
  analyticsChatbot: 'Analytics Chatbot',
  analyticsDashboardUpdateInfo:
    'Analytics Dashboards are refreshed every 3 hours',
  analyticsProjectAlertContent: (numberOfProjects: number) =>
    `Showing data from ${numberOfProjects} projects`,
  analyticsProjectAlertTooltip:
    'Dashboards show data from the projects you have access to',
  android: 'Android',
  aNewCompanyWillBeCreated: 'A new company will be created',
  aNewVersion: 'A new version available',
  annotation: 'Annotation',
  annotations: 'Annotations',
  annotationsUpdated: 'Annotations updated',
  answer: 'Answer',
  answeredByLabel: (name: string) => `Answered by ${name}`,
  answerHere: 'Answer here',
  answerRequiredQuestions: 'Answer the required questions for the Permit',
  answers: 'Answers',
  answersCombinedWith: 'Answers combined with',
  answersNotSubmittedYet: 'Answers not submitted yet',
  answerUsedByRule: 'Used by Conditional Logic',
  any: 'Any',
  anytime: 'Anytime',
  apiKeyCreated: 'API Key Created',
  apiKeyCreation: 'New API Key',
  apiKeyDeleted: 'API Key Deleted',
  apiKeyDeletionConfirmation: 'API Key Deletion Confirmation',
  apiKeys: 'API Keys',
  apiKeySecretWarning:
    ' Please make sure to copy the client secret. As it will not ever be displayed afterwards.',
  appliedTo: 'Applied to',
  apply: 'Apply',
  applyLocationSettingsForThisTemplate:
    'Apply location settings for this template',
  appraisalAlreadyCompleted:
    'Please note that the appraisal for this document has already been completed.',
  appraisals: 'Appraisals',
  appraisalsByTemplateName: (templateName: string) =>
    `${templateName} Appraisal`,
  appraisalSubmitted: 'Appraisal submitted successfully',
  appraisedBy: 'Appraised By',
  approval: 'Approval',
  approvalConditionalLogicDescription:
    'Set the conditions that will trigger this approval.',
  approvalNomination: 'Approvers Nominated',
  approvalNoRuleText: 'There is no rules associated with this approval.',
  approvalOrder: 'Approval Order',
  approvalOrderText:
    'Is there any order that you would like the approval to take place?',
  approvalProgressText: (doneReviews: number, allReviews: number) =>
    `${doneReviews} out of ${allReviews} - View Approvals`,
  approvalRequest: 'Approval Request',
  approvalRequestInfo: 'Issue the permit to begin work on site',
  approvalRequestInfoChecklist: 'Approve this checklist to complete the review',
  approvalRequestInfoDraft: `You've already begun the approval process for this permit. To continue, click 'Approve'.`,
  approvals: 'Approvals',
  approvalStageChangeWarning:
    'This action cannot be completed until all approval rules are deleted.',
  approve: 'Approve',
  approveCompany: 'Approve Company',
  approveCompliance: 'Approve Compliance',
  approved: 'Approved',
  approvedBy: 'Approved By',
  approvedExtension: 'Extension Approved',
  approvedPendingCompliance: 'Approved (Pending Compliance)',
  approveExtension: 'Approve Extension',
  approvePermit: 'Approve Permit',
  approvePermitReceiveDescription:
    'Configure which groups can receive permits on creation and when transferred',
  approvePermitReceiveTitle: 'Who can receive the permit?',
  approvePermitTransfersTitle: 'Who can approve permit transfers?',
  approver: 'Approver',
  approverQuestions: 'Approver Questions',
  approvers: 'Approvers',
  approveTransfer: 'Approve Transfer',
  appUpdatedSuccessfully: 'App Updated Successfully',
  archive: 'Archive',
  archived: 'Archived',
  archivedChecklistTemplate: 'Checklist Template Archived',
  archivedLocation: 'Archived Location',
  archivedPermitTemplate: 'Archived Permit Template',
  archivedPlan: 'Archived Plan',
  archiveItems: 'Archive Items',
  areaLocation: 'Area Location',
  areYouSure: 'Are you sure?',
  areYouSureArchiveLocation: 'Are you sure you want to archive?',
  areYouSureDeleteResponse: 'Are you sure you want to delete this response?',
  areYouSureYouWantToDeleteThisCompany:
    'Are you sure you want to delete this company?',
  artificialPhotoText: 'Looks like this photo was not taken live',
  assign: 'Assign',
  assignCompliance: 'Assign Compliance',
  assignComplianceRequirements: 'Assign compliance requirements',
  assigned: 'Assigned',
  assignedPerson: 'Assigned Person',
  assignedTo: 'Assigned To',
  assignee: 'Assignee',
  assigneeAToZ: 'Assignee (A-Z)',
  assigneeRequiresProjectContext:
    'Assignee requires a project context for this register.',
  assigneeZToA: 'Assignee (Z-A)',
  assignment: 'Assignment',
  assignTo: 'Assigned To',
  assignToUserProperty: 'Assign to User Property',
  assignUserToProjects: (name: string) => `Assign ${name} to projects:`,
  assumeTaskRole: 'Assume Task Role',
  attachmentAdded: 'Attachment added',
  attachmentInputDragPrompt:
    'Or drag & drop, or paste files in this area using Ctrl/Cmd+V',
  attachmentProperty: 'Attachment Property',
  attachments: 'Attachments',
  attachmentsAdded: 'Attachments added',
  attachmentsAddedSuccessfully: 'Attachments added successfully',
  attachmentsCount: (count: number) =>
    `${count} Attachment${count === 1 ? '' : 's'}`,
  attachPDF: 'Attach PDF',
  attention: 'Attention',
  audio: 'Audio',
  audioOnly: 'Audio only',
  audioTranscript: 'Audio Transcript',
  authentication: 'Authentication',
  authenticationDeletion: 'Authentication Deletion',
  authenticationDeletionConfirmation:
    'Are you sure you want to delete organisation authentication configuration?',
  authGroupChecklistPermissionsUpdated:
    'Auth Group Checklist Permissions Updated',
  authGroupCreated: 'Auth Group Created',
  authGroupDeleted: 'Auth Group Deleted',
  authGroupNameUpdated: 'Auth Group Name Updated',
  authGroupPermitPermissionsUpdated: 'Auth Group Permit Permissions Updated',
  authGroupRegisterPermissionsUpdated:
    'Auth Group Register Permissions Updated',
  authGroups: 'Auth Groups',
  authGroupTemplateRolesText:
    'By selecting one of the below roles, this means that only the users added to this Authorisation Group will be able to be briefed onto this permit type under the specified role',
  author: 'Author',
  authorCompany: 'Author Company',
  authorisation: 'Authorisation',
  authorisationGroup: 'Authorisation Group',
  authorisationGroups: 'Authorisation Groups',
  authorisedPermits: 'Authorised Permits',
  authorisedPermitsToolTip:
    'People in this group will be able to Issue these Permits',
  authorisedPerson: 'Authorised Person',
  authorisedPersonQuestions: 'Authorised Person Questions',
  authorisedPersonTooltip:
    'The Authorised Person or Project Admin will start the process and issue the permit to a Permit Holder.',
  authPersonQuestions: 'Authorised Person Questions',
  automatedEvent: 'Automated Event',
  automatedInformation: 'Automated Information',
  automaticallyAppliesToAllItems: 'Automatically applies to all items',
  automaticallySetToMaximumDuration: 'Automatically set to maximum duration',
  autoRefreshEveryMinute: 'Auto Refresh (every minute)',
  autosavedAt: (time: string) => `Autosaved at ${time}`,
  awaitingApproval: 'Awaiting Approval',
  awaitingApprovalNotification: 'Awaiting Approval Notification',
  awaitingFinalSignOff: 'Final Sign Off',
  awaitingParentPermit: 'Awaiting Parent Permit',
  awaitingParticipantSignatures: 'Awaiting Participant Signatures',
  awaitingPostPermitChecks: 'Post Permit Checks',
  back: 'Back',
  backToForm: 'Back to Form',
  backToHome: 'Back to Home',
  backToLogin: 'Back to login',
  backToOrganisation: 'To Organisation',
  backToProject: 'Back to Project',
  baseTemplate: 'Base Template',
  basicInformation: 'Basic Information',
  basicInformationDescription: 'Enter some basic information about the Permit',
  beBriefedAs: 'Be briefed as',
  beforePermitHolder: 'Before Permit Holder',
  bestUserExperienceProjectPlan:
    'For the best experience view this plan in horizontal view',
  beta: 'Beta',
  betaAccess: 'Beta access',
  boolean: 'Boolean',
  boundaryConfigured: 'Boundary Configured',
  boundaryRemoved: 'Boundary Removed',
  boundarySaved: 'Boundary Saved',
  briefAndCollectSignatures: 'Brief & Collect Signatures',
  briefAndSign: 'Brief & Sign',
  briefed: 'Briefed',
  briefedParticipant: 'Briefed Participant',
  briefExistingParticipants: 'Brief Existing Participants',
  briefExistingParticipantsDescription:
    'Brief participants already added to the permit',
  briefingAcceptanceDescription:
    'You have been added to this permit as a participant. Review the permit details, then click Accept to confirm.',
  briefingAccepted: 'Briefing accepted',
  briefingAttendanceRecord: 'Briefing Attendance Record',
  briefingAttendanceRecordDescription:
    'Please select your role and submit your signature to capture your acknowledgement and understanding of the briefing disclaimer and contents.',
  briefingAttendanceRecordExternalDescription:
    'Please enter your details in order to capture the briefing attendance record.',
  briefingAttendanceRecordExternalSigning:
    'Please submit your signature to capture your acknowledgement and understanding of the briefing disclaimer and contents.',
  briefingDeclined: 'Briefing declined',
  briefingDescription:
    'This briefing material must be shown to any new participants joining',
  briefingDiscarded: 'Briefing discarded',
  briefingDisclaimer: 'Briefing Disclaimer',
  briefingInformation: 'Briefing Information',
  briefingLog: 'Briefing Log',
  briefingParticipant: 'Briefing Participant',
  briefingParticipants: 'Briefing Participants',
  briefings: 'Briefings',
  briefingText: 'Briefing Text',
  briefParticipant: 'Brief Participant',
  briefParticipants: 'Brief Participants',
  briefParticipantsChecklistDescription:
    'Brief participants onto this checklist',
  briefParticipantsInspectionDescription:
    'Brief participants onto this inspection',
  briefParticipantsPermitDescription: 'Brief participants onto the permit',
  briefSameRole: 'Brief Same Role',
  briefTask: 'Brief Task',
  browseFile: 'Browse File',
  bulkDownload: 'Bulk Download',
  bulkMerge: 'Bulk Merge',
  bulkUpload: 'Bulk Upload',
  businessUnit: 'Business Unit',
  businessUnitCreated: 'Business Unit Created',
  businessUnitDeleted: 'Business Unit Deleted',
  businessUnitOverview: 'Overview',
  businessUnits: 'Business Units',
  businessUnitUpdated: 'Business Unit Updated',
  by: 'By',
  byLocation: 'By Location',
  bypassed: 'Bypassed',
  bypassReview: 'Bypass Review',
  bySigningText:
    'By signing this you are requesting a permit.\n' +
    'We will send it to your project admin so that they can review it.',
  byWho: (firstName: string, lastName: string) => `By ${firstName} ${lastName}`,
  calculated: 'Calculated',
  calculatedByFormula: 'Calculated by formula',
  calculateDurationBasedOnDays: 'Calculate duration based on days',
  calibrate: 'Calibrate',
  calibrateMapInstructions:
    "Move the map and press calibrate when you're happy with the position",
  camera: 'Camera',
  cameraAccess: 'Camera access',
  cameraAccessMessage: 'SiteAssist needs camera access to capture proof',
  cameraAndfileReadAccess: 'Camera and File Read access',
  cameraAndFileReadAccessMessage:
    'SiteAssist needs camera and file read access to upload proofs',
  canAddParticipantsWithoutBriefing: 'Can add participants without briefing?',
  canAddParticipantsWithoutBriefingHint:
    '⚠️ When set to Yes, briefing signatures, photo verifications and geolocation will be skipped.',
  cancel: 'Cancel',
  cancelExtensionRequest: 'Cancel Extension Request?',
  cancellationReason: 'Cancellation Reason',
  cancelled: 'Cancelled',
  cancelPermit: 'Cancel Permit',
  cancelPermitDescription: 'Mark this permit as no longer valid',
  cancelPermitReason: 'Please provide the reason for cancelling the permit.',
  cancelReason: 'Cancel Reason',
  cancelRequest: 'Cancel Request',
  cancelRequestExtension: 'Cancel Request Extension',
  cancelRequestExtensionConfirmation:
    'Are you sure you want to cancel the extension request?',
  cancelRequestExtensionConfirmationMessage: (extensionEndsOn: string) =>
    `You’re about to cancel the request to extend this permit to ${extensionEndsOn} .`,
  cancelTask: 'Cancel Task',
  cancelTaskDescription: 'Mark this task as cancelled',
  cancelTransfer: 'Cancel Transfer',
  canCompaniesAccessEachOthersInformation:
    "Can companies access each other's information?",
  cannotAddSubLocation: 'Cannot Add Sub Location',
  cannotAddSubLocationMessage:
    'You cannot add a sub-location to a location with a plan',
  cannotClosePermit: 'Cannot close permit',
  cannotClosePermitWithOpenChildren: (count: number) =>
    `This permit has ${count} linked permit${count === 1 ? '' : 's'} that ${
      count === 1 ? 'is' : 'are'
    } not yet closed, cancelled, rejected, or discarded. Please resolve ${
      count === 1 ? 'it' : 'them'
    } before closing.`,
  cannotEditLocationsMessage:
    'You cannot edit locations, only Project Admins are allowed to edit Site Locations',
  cannotEditTemplateMessage:
    "To make changes to these settings, please contact your organisation's Account Owner, or help@siteassist.co.uk",
  cannotUploadPlan: 'Cannot Upload Plan',
  cannotUploadPlanMessage:
    'You cannot upload a plan to a location with sub-locations',
  capturedBy: 'Captured by',
  captureLocationAction: 'capture a location',
  captureMode: 'Capture mode',
  captureModeAudioDescription: 'Voice note, no footage captured',
  captureModePhotoDescription: 'Photos and optional voice note',
  captureModes: 'Capture modes',
  captureModesDescription:
    'What a submitter can capture on the capture screen.',
  captureModeVideoDescription: 'Camera and audio capture',
  change: 'Change',
  changeAuthGroups: 'Change Authorisation Groups',
  changedProfileInformation: 'Changed Profile Information',
  changedProjectExternalCode: 'Project External Code Changed',
  changedProjectStatus: 'Project Status Changed',
  changePassword: 'Change Password',
  changePermissions: 'Change Permissions',
  changePermitHolderInformation: 'Change Permit Holder Information',
  changeQuestionSetMessage:
    'Changing the question set will will remove all existing rules for this approval. Do you want to continue?',
  changeQuestionSetQuestion: 'Change question set?',
  changeResponse: 'Change Response',
  changeRole: 'Change Role',
  changesAddressed: 'Changes Addressed',
  changesAddressedNotifications: 'Changes addressed',
  changesDone: 'Changes Done',
  changesRequested: 'Changes Requested',
  changesRequestedNotifications: 'Changes requested',
  changeStatusToDraft: 'Change Status to Draft',
  changeTemplates: 'Change Templates',
  characterCount: 'Character count',
  characters: 'characters',
  chartFailedToLoad: 'This chart could not be loaded',
  checkedBy: 'Checked By',
  checkedIn: 'Checked In',
  checkedInAt: (time: string) => `Checked in at ${time}`,
  checkedInBy: (name: string) => `Checked in by ${name}`,
  checkedInCount: (count: number) => `Checked In (${count})`,
  checkedInUsers: 'Checked-In Users',
  checkedOut: 'Checked Out',
  checkedOutBy: (name: string) => `Checked out by ${name}`,
  checkedOutCount: (count: number) => `Checked Out (${count})`,
  checkIn: 'Check In',
  checkInActivity: 'Check-in Activity',
  checkInExtended: 'Check In Extended',
  checkinLocation: 'Check In Location',
  checkInOthersDescription: 'Check participants in on this permit',
  checkInOthersWording:
    "Obtain the user's signature before tapping the check-in button to confirm they are on site and adhering to the permit's risk assessments and method statements.",
  checkInOut: 'Check In / Check Out',
  checkInParticipants: 'Check In Participants',
  checkinPrompt:
    'Please use the button below to indicate your location on the map',
  checkInRequired: 'Check In Required',
  checkInSelfDescription: 'Record that you are on site under this permit',
  checkInStandardWording:
    "By checking in, you confirm you are on site and working under this permit's risk assessments and method statements.",
  checkInTime: 'Check In Time',
  checklist: 'Checklist',
  checklistAnswerRequiredQuestions:
    'Answer the required questions for the Checklist',
  checklistApproved: 'Checklist Approved',
  checklistAuthor: 'Checklist Author',
  checklistBasicInformationDescription:
    'Enter some basic information about the Checklist',
  checklistBriefingPromptDescription:
    'If you are trying to get briefed on a checklist please ask the person who is doing the briefing to use <span class="text-bold">Actions -> Brief Participants -> Via QR Code</span>.',
  checklistBriefingPromptTitle: 'Is this a briefing?',
  checklistChangesRequested: 'Checklist changes are requested',
  checklistCompleted: 'Checklist Completed',
  checklistDetails: 'Checklist Details',
  checklistDownloadComplete: 'Checklist Download Complete',
  checklistDownloadFailed: 'Checklist Download Failed',
  checklistDraftCreated: 'Checklist Draft Created',
  checklistDraftDeleted: 'Checklist Draft Deleted',
  checklistDraftSaved: 'Checklist Draft Saved',
  checklistDraftUpdated: 'Checklist Draft Updated',
  checklistFilter: 'Checklists Filter',
  checklistId: 'Checklist ID',
  checklistInformation: 'Checklist Information',
  checklistInitiationPermissionMessage:
    'When this permission is applied, a user must be in an Authorisation Group that has this permission enabled to raise the checklist. Any Users not in one of these Auth Groups will not be able to raise this checklist template.',
  checklistList: 'Checklist List',
  checklistPermissions: 'Checklist Permissions',
  checklistQRCodeDescription:
    'Scan this QR code with your phone camera to open the checklist.',
  checklistQuestions: 'Checklist Questions',
  checklistRejected: 'Checklist Rejected',
  checklistRole: 'Checklist Role',
  checklists: 'Checklists',
  checklistSubmitted: 'Checklist Submitted',
  checklistSummary: 'Checklist Summary',
  checklistTemplate: 'Checklist Template',
  checklistTemplates: 'Checklist Templates',
  checklistTimeline: 'Checklist Timeline',
  checklistType: 'Checklist Type',
  checkOthersInOut: 'Check Others In / Out',
  checkOut: 'Check Out',
  checkOutOthersDescription: 'Check participants out of this permit',
  checkOutParticipants: 'Check Out Participants',
  checkOutStandardWording:
    'Before you leave, make sure your work area is safe and any required checks are complete.',
  checkoutTime: 'Checkout Time',
  checksComplete: 'Checks Complete',
  checkSelfInOut: 'Check Self In / Out',
  checkSpamFolder: "If you don't see it, check your spam folder",
  checkUpdates: 'Check Updates',
  checkYourEmail: 'Check your email',
  childEndDateOutsideParent:
    "End date must be on or before the parent permit's end date",
  childPermit: 'Child Permit',
  childPermits: 'Child Permits',
  childPermitsSummary: (count: number, names: string) =>
    names ? `Child Permits: ${count} (${names})` : `Child Permits: ${count}`,
  childStartDateOutsideParent:
    "Start date must be on or after the parent permit's start date",
  chooseFolder: 'Choose Folder',
  chooseFromSitebox: 'Choose from SiteBox',
  choosePermitHolder: 'Choose the Permit Holder',
  chooseUpTo3PropertiesToShowAsLabels:
    'Choose up to 3 properties to show as labels',
  chooseWhenAndHowOftenInspection:
    'Choose when and how often this inspection should be carried out',
  city: 'City',
  clear: 'Clear',
  clearAll: 'Clear all',
  clearSignature: 'Clear Signature',
  clearSort: 'Clear Sort',
  clickAnywhereOnThePlanToPlaceYourPin:
    'Click anywhere on the plan to place your pin',
  clickLinkToFinishSetup:
    'Click the link in the email to finish setting up your account.',
  clickOnThePurplePinToSeeAdditionalOptions:
    'Click on the purple pin to see additional options',
  clickThePlusButtonTo: (action: string) =>
    `Click the plus button to ${action}`,
  clickThisButtonToPutAPinOnThePlan:
    'Click this button to put a pin on the plan',
  clientId: 'Client ID',
  clientSecret: 'Client secret',
  clonable: 'Clonable',
  clone: 'Clone',
  clonedPermitDetected: 'Cloned Permit Detected',
  clonedToNewDraft: 'Cloned to a new draft',
  clonePermit: 'Clone Permit',
  clonePermitDescription: 'Clone this permit to a new draft',
  cloneToNewDraftDescription: 'Clone to a new draft',
  close: 'Close',
  closeAndSave: 'Close & Save',
  closed: 'Closed',
  closeObservation: 'Close Observation',
  closeObservationDescription: 'Mark the Observation as complete',
  closeObservationReason: 'Closing Reason',
  closeObservationReasonDescription:
    'Please provide a reason for closing the observation.',
  closePermit: 'Close Permit',
  closePermitDescription: 'Declare this permit complete',
  closePermitParagraph:
    'Are you sure? This Permit will no longer be open and work has to stop immediately.',
  closeShape: 'Close Shape',
  code: 'Code',
  collectSignature: 'Collect Signature',
  collectSignatures: 'Collect Signatures',
  colorProperty: 'Color Property',
  colorPropertyDescription:
    'Choose a property to color-code items on the plan.',
  colour: 'Colour',
  comment: 'Comment',
  commentedOnAQuestion: 'commented on a question',
  commentLowerCase: 'comment',
  comments: 'comments',
  commentsCount: (count: number) => `${count} Comment${count === 1 ? '' : 's'}`,
  commentsTitle: 'Comments',
  companies: 'Companies',
  company: 'Company',
  companyAdmin: 'Company Admin',
  companyAdminAdded: 'Company Admin Added',
  companyApprovalRequest: 'Company Approval Request',
  companyComplianceCompanyActivated: 'Company activated',
  companyComplianceRequirementApproved: 'Compliance requirement approved',
  companyComplianceRequirementExpired: 'Compliance requirement expired',
  companyComplianceRequirementRejected: 'Compliance requirement rejected',
  companyComplianceRequirementReuploaded: 'Compliance requirement re-uploaded',
  companyComplianceRequirementUnassigned: 'Compliance requirement unassigned',
  companyComplianceRequirementUploaded: 'Compliance requirement uploaded',
  companyDeleted: 'Company Deleted',
  companyEdited: 'Company Edited',
  companyInformation: 'Company Information',
  companyIsNowActive: 'Your company is now active',
  companyMerge: 'Company Merge',
  companyName: 'Company Name',
  companyNamePropertyDescription:
    'This property is required and cannot be deleted',
  companyOnboardingAdminAssigned: 'Company admin assigned',
  companyOnboardingApproved: 'Company approved',
  companyOnboardingChangesRequested: 'Changes requested',
  companyOnboardingCompanyAddedToNamedProject: (projectName: string) =>
    `Company added to ${projectName}`,
  companyOnboardingCompanyAddedToProject: 'Company added to project',
  companyOnboardingFormSubmitted: 'Company onboarding form submitted',
  companyOnboardingRejected: 'Company rejected',
  companyOnboardingResubmitted: 'Company resubmitted for approval',
  companyOnboardingSubmitted: 'Company submitted for approval',
  companyOnboardingUserAssigned: 'User added to Company',
  companyPropertyCreated: 'Company property created',
  companyPropertyDeleted: 'Company property deleted',
  companyPropertyUpdated: 'Company property updated',
  companyRequired: 'Company is required',
  companyRole: 'Company Role',
  companySettings: 'Company Settings',
  competencies: 'Competencies',
  competenciesProgress: (completed: number, total: number) =>
    `${completed}/${total} Completed`,
  competenciesStatus: 'Competencies Status',
  competenciesTemplates: 'Competencies Templates',
  competency: 'Competency',
  competencyBannerComplete: 'Complete Now',
  competencyBannerCount: (count: number) =>
    count === 1 ? '1 Competency Required' : `${count} Competencies Required`,
  competencyCompleteInduction: 'Complete Induction',
  competencyEmpty: 'No competencies have been assigned to this user.',
  competencyExpiryWarningBody:
    'Retake the induction before it expires to keep access to permits and site activities.',
  competencyExpiryWarningRetake: 'Retake Now',
  competencyExpiryWarningTitle: (name: string, days: number) =>
    days === 1
      ? `Your ${name} expires tomorrow`
      : `Your ${name} expires in ${days} days`,
  competencyFailedToLoad: 'Failed to load competencies.',
  competencyHomeBlockDescription:
    'All training modules must be completed to access app features.',
  competencyHomeBlockTitle: 'Competencies Required',
  competencyInductionAnswerRequired: 'Please select an answer to continue.',
  competencyInductionContentStageLabel: 'Content',
  competencyInductionEstimatedDuration: (minutes: number) =>
    `Estimated duration: ${minutes} minutes`,
  competencyInductionInvalidAnswerBody:
    'Your answer is incorrect. Please review the training content and try again.',
  competencyInductionInvalidAnswerCaption: 'Invalid answer',
  competencyInductionQuestionProgress: (current: number, total: number) =>
    `${current} of ${total}`,
  competencyInductionRequired: 'Induction Required',
  competencyInductionSuccess: 'Induction completed.',
  competencyInductionUnavailable:
    'This competency is not configured yet. Please contact your administrator.',
  competencyName: 'Competency Name',
  competencyNotificationActivated: (name: string) => `Complete your ${name}`,
  competencyNotificationExpired: (name: string) =>
    `Your ${name} has expired - action required`,
  competencyNotificationExpiring30Days: (name: string, date: string) =>
    `Your ${name} expires on ${date}`,
  competencyNotificationExpiring7Days: (name: string) =>
    `Your ${name} expires in 7 days - action required`,
  competencyNotificationVersionPublished: (name: string, date: string) =>
    `The ${name} has been updated - retake by ${date}`,
  competencyRetake: 'Retake Competency',
  competencyRetakeInduction: 'Retake Induction',
  competencyStart: 'Start Competency',
  competencyStatusCompleted: 'Completed',
  competencyStatusExpired: 'Expired',
  competencyTemplate: 'Competency Template',
  competencyTemplateActivated: 'Competency template version activated',
  competencyTemplateArchived: 'Competency template archived',
  competencyTemplateCreated: 'Competency template created',
  competencyTemplateDiscarded: 'Competency template version discarded',
  competencyTemplateUpdated: 'Competency template updated',
  competencyTemplateVersionUpdated: 'Competency template version updated',
  competencyVersionActivateGracePeriodNote: (previousVersion: number) =>
    `This will start a 30-day retake grace period for users on V${previousVersion}.`,
  competencyVersionAssignmentInfo:
    'On activation, all current organisation users are assigned. New users joining the organisation are automatically assigned.',
  competencyVersionAssignToAllUsers: 'Assign to all users in the organisation',
  competencyVersionCannotActivate:
    'Complete the configuration and add at least one quiz question before activating.',
  competencyVersionContentDescription: 'Content Description',
  competencyVersionContentUrl: 'Content URL',
  competencyVersionContentUrlRequired: 'Content URL is required',
  competencyVersionEstimatedDuration: 'Estimated Duration',
  competencyVersionEstimatedDurationHours: 'Hours',
  competencyVersionEstimatedDurationMinutes: 'Minutes',
  competencyVersionExpireToggleLabel:
    'Does this training expire after completion?',
  competencyVersionExpiryDays: 'Days',
  competencyVersionExpiryMonths: 'Months',
  competencyVersionExpiryTime: 'Expiry Time',
  competencyVersionExpiryYears: 'Years',
  competencyVersionLabel: 'Version',
  complete: 'Complete',
  completeAtPointOfWorks:
    'You must complete this document at the point of works using a mobile device',
  completed: 'Completed',
  completedBy: 'Completed By',
  completedChecks: 'Completed Checks',
  completedDate: 'Completed Date',
  completedInspections: 'Completed Inspections',
  completedObservations: 'Completed Observations',
  completedRuns: 'Completed Runs',
  completePostPermitChecks: 'Complete Post Permit Checks',
  completeProfile:
    'Hi <strong>{0}</strong>, you’ve been added as an <span class="text-bold">{1}</span> in the <span class="text-bold">{2}</span> Organisation. Please confirm a few details to get started.',
  completeProfileMobile:
    'Hi {0}, you’ve been added as an {1} in the {2} Organisation. Please confirm a few details to get started.',
  completeProfileSelfRegistered: 'Please confirm a few details to get started.',
  completeTask: 'Complete Task',
  completeYourProfile: 'Complete Your Profile',
  completion: 'Completion',
  compliance: 'Compliance',
  complianceApprovedSuccess: 'Compliance approved',
  complianceAssignedSuccess: 'Compliance requirements assigned',
  complianceDocumentExpired: 'Compliance Document Expired',
  complianceInformation: 'Compliance Information',
  complianceRejected: 'Compliance Rejected',
  complianceRejectedSuccess: 'Compliance rejected',
  complianceRequirement: 'Compliance Requirement',
  complianceRequirementCreated: 'Compliance requirement created',
  complianceRequirementDeleted: 'Compliance requirement deleted',
  complianceRequirementExpiringIn: (days: number) =>
    `Compliance requirement expires in ${days} day${days === 1 ? '' : 's'}`,
  complianceRequirementRejectedNotification: 'Compliance requirement rejected',
  complianceRequirements: 'Compliance Requirements',
  complianceRequirementsApprovalRequired:
    'Compliance Requirements Approval Required',
  complianceRequirementsNeeded: 'Compliance Requirements Needed',
  complianceRequirementsReordered: 'Compliance requirements reordered',
  complianceRequirementTitle: 'Title',
  complianceRequirementTitlePlaceholder: 'e.g. Public Liability Insurance',
  complianceRequirementTitleTooLong: 'Title must be at most 255 characters',
  complianceRequirementUpdated: 'Compliance requirement updated',
  complianceRequirementUploadedForReview:
    'Compliance requirement uploaded for review',
  complianceType: 'Compliance Type',
  complianceUnassignedSuccess: 'Compliance requirement unassigned',
  complianceUploadedSuccess: 'Compliance document uploaded',
  compressingVideo: 'Compressing Video',
  condition: 'Condition',
  conditionalLogic: 'Conditional Logic',
  conditionalQuestion: 'Conditional Question',
  conditionalQuestionGroup: 'Conditional Question Group',
  conditionalQuestionGroupExplanation:
    'If answering "{0}", you will proceed to Question {1}',
  conditionalQuestionGroupTip:
    'The permit requester will skip this question group, depending on their answer',
  conditionIndex: (index: number) => `Condition ${index}`,
  conditionsChipText: (length: number) =>
    `Conditions: (${length} rule${length !== 1 ? 's' : ''})`,
  conditionsQuestionsChipText: (length: number) =>
    `Questions: (${length} question${length !== 1 ? 's' : ''})`,
  conditionType: 'Condition Type',
  configuration: 'Configuration',
  configurePostPermitChecksText: 'Click here to configure Post Permit Checks',
  configureTemplateSettings: 'Configure the settings for your template.',
  configureTemplateSettingsText: 'Configure the settings for your template.',
  confirm: 'Confirm',
  confirmAcknowledgeText: 'You must acknowledge this document to proceed.',
  confirmActivateTemplateVersion:
    'Are you sure you want to activate this version? Everything created from this template from now on will use it.',
  confirmAreas: (featureCount: number) => `Confirm ${featureCount} Area(s)`,
  confirmDeleteBusinessUnit:
    '<p>Are you sure you want to <span class="text-red text-bold">delete</span> business unit <span class="text-red text-bold">{0}</span>?</p>',
  confirmDeleteGroup:
    'Are you sure you want to <span class="text-red text-bold">delete</span> <span class="text-bold">{0}</span>? This action can not be undone.',
  confirmDeleteLayer: 'Are you sure you want to delete this layer?',
  confirmDeleteLocation:
    '<p>Are you sure you want to <span class="text-red text-bold">delete</span> this location? Your changes will not be saved until you save locations.</p>',
  confirmDeleteMember:
    'Are you sure you want to <span class="text-red text-bold">delete</span> <span class="text-bold">{0} {1}</span> from group <span class="text-bold">{2}</span>? This action can not be undone.',
  confirmDeleteOrganisationLink: 'Are you sure you want to delete this link?',
  confirmDeletePermitRole:
    'Are you sure you want to delete this role? This action can’t be undone.',
  confirmDeleteProject:
    'Are you sure you want to <span class="text-red text-bold">delete</span> the template from <span class="text-bold">{0}</span>?',
  confirmDeleteProjectFromBusUnit:
    'Are you sure you want to delete this project from the business unit?',
  confirmDeleteQuestion: 'Are you sure you want to delete this question?',
  confirmDeleteQuestionGroup:
    '<p>Are you sure you want to <span class="text-red text-bold">delete</span> this question group? Your changes will not be saved until you save the template.</p>',
  confirmDeleteTaskStep: 'Are you sure you want to delete this step?',
  confirmDeleteTemplate:
    'Are you sure you want to <span class="text-red text-bold">delete</span> <span class="text-bold">{0}</span> from the project?',
  confirmDeleteTemplateVersion:
    'Are you sure you want to delete this version? This action can’t be undone.',
  confirmDeleteText:
    "Are you sure you want to <span class='text-red text-bold'>delete</span> this question?",
  confirmDeleteUser:
    'Are you sure you want to <span class="text-red text-bold">delete</span> <span class="text-bold">{0} {1}</span>? This action can not be undone.',
  confirmInitiatorRoleChange:
    'Are you sure you want to change the initiator role? This might cause some data loss.',
  confirmLeave:
    'You have unsaved changes. If you leave now, your changes will be discarded.',
  confirmLeavePage:
    'You have unsaved changes. Are you sure you want to leave this page?',
  confirmLeavePageSaveDraft:
    'You have unsaved changes. Are you sure you want to leave this page and discard this draft?',
  confirmLocation: 'Confirm Location',
  confirmNewPassword: 'Confirm new password',
  confirmPassword: 'Confirm Password',
  confirmRemoveBoundary:
    'Are you sure you want to remove this boundary? Map interactions tied to this location will no longer auto-centre here.',
  confirmResendInviteUser:
    'Are you sure you want to <span class="text-bold">resend</span> an invite to <span class="text-bold">{0} {1}</span>?',
  confirmSaveTemplate: 'Are you sure you want to save this template?',
  confirmSignature: 'Confirm Signature',
  confirmStopSchedule:
    'Are you sure you want to stop this inspection schedule?',
  confirmToContinueRaisingAPermit: 'Confirm to continue raising permit',
  confirmUnsavedChanges:
    'You have made some changes to the version. You must save the version before you can Activate it',
  connectThisProjectToProcore: 'Connect this project to Procore',
  contactNumber: 'Contact Number',
  contactParticipant: 'Contact Participant',
  contains: 'Contains',
  context: 'Context',
  continue: 'Continue',
  continueOnWeb: 'Continue on Web',
  continueReview: 'Continue Review',
  continueToCollectSignatures:
    'Continue to collect signatures and/or edit the participants',
  contractor: 'Contractor',
  contractorAppraisal: 'Contractor Appraisal',
  controlsQuestionsVisibilityInCurrentStage: (n: number) =>
    `Controls ${n} question(s) visibility in current stage`,
  controlsQuestionsVisibilityInStage: (n: number, stageName: string) =>
    `Controls ${n} question(s) visibility in ${stageName} stage`,
  copied: 'Copied!',
  copiedToClipBoard: 'Copied to Clipboard!',
  copyChecklistLink: 'Copy Checklist Link',
  copyChecklistLinkToClipboard:
    'Copy a link to this checklist to the clipboard',
  copyLink: 'Copy Link',
  copyLinkDescription: 'Only logged in SiteAssist users can open this link.',
  copyLinkToChecklist: 'Copy Link to Checklist',
  copyLinkToPDF: 'Copy Link to PDF',
  copyLinkToPDFDescription:
    'No SiteAssist user account is required. Link expires in 30 days.',
  copyLinkToPermit: 'Copy Link to Permit',
  copyLinkToPermitDescription:
    'Only logged in SiteAssist users can view the permit.',
  copyPermitLink: 'Copy Permit Link',
  copyPermitLinkToClipboard: 'Copy a link to this permit to the clipboard',
  copyTaskLink: 'Copy Task Link',
  copyTaskLinkToClipboard: 'Copy a link to this task to the clipboard',
  copyToClipboard: 'Copy to Clipboard',
  couldNotLoadSignature: 'Could not load signature.',
  couldNotRetrieveEmail: 'Could not retrieve email. Please try again',
  couldNotSaveForLater: "Couldn't save this for later. Please try again.",
  couldNotSaveSchedule: 'Could not save schedule',
  countdown: 'Countdown',
  countdownDescription:
    'The Requester will have to wait for the countdown to finish before they can finish post permit checks.',
  countdownName: 'Countdown Name',
  countdownQuestionAlreadyInUseText:
    'This question type already exists in the template, and can only appear once',
  countdownQuestionBeenUsedText:
    'A countdown question type is being used in the template. Please remove the question before removing the countdown.',
  countdownQuestionNotConfiguredInPostPermitText:
    'Post Permit Checks Countdown has not been enabled for this template.',
  countdownText:
    'Set a period of time which must pass before post permit checks can be complete.',
  countdownTime: 'Countdown Time',
  create: 'Create',
  createAuthGroup: 'Create Authorisation Group',
  createBriefingSession: 'Create Briefing Session',
  createBusinessUnit: 'Create Business Unit',
  createCheck: 'Create Check',
  createChecklist: 'Create Checklist',
  createChecklistDraft: 'Create Checklist Draft',
  createChildPermit: 'Create Child Permit',
  createChildPermitDescription: 'Link a child to the parent permit',
  created: 'Created',
  createdBy: 'Created by',
  createdChecklistTemplate: 'Checklist Template Created',
  createdChecklistVersion: 'Checklist Version Created',
  createdDate: 'Created Date',
  createDigest: 'Create Digest',
  createdOn: 'Created On',
  createdOnAscending: 'Created (oldest first)',
  createdOnDescending: 'Created (newest first)',
  createdPermitTemplate: 'Permit Template Created',
  createdPermitTemplateVersion: 'Permit Version Created',
  createGroup: 'Create Group',
  createInspection: 'Create Inspection',
  createItem: (item: string) => `Create ${item}`,
  createItems: 'Create Items',
  createNewChecklist: 'Create New Checklist',
  createNewChecklistDescription: 'Start a new checklist',
  createNewCompany: 'Create new company',
  createNewDraft: 'Create New Draft',
  createNewInspection: 'Create New Inspection',
  createNewInspectionDescription: 'Start a new inspection',
  createNewPermit: 'Create New Permit',
  createNewPermitDescription: 'Start a new permit',
  createNewTag: 'Create new tag',
  createNewTask: 'Create New Task',
  createNewTaskDescription: 'Start a new task',
  createNewTemplateRole: 'Create New Role',
  createNewVersion: 'Create New Version',
  createNewView: 'Create New View',
  createOrganisation: 'Create Organisation',
  createPasswordInfoText: 'You need to create a password before you can login.',
  createPasswordWelcomeMessage:
    'Welcome to SiteAssist! Please set your password to confirm your account.',
  createPasswordWelcomeText:
    'Welcome to SiteAssist, please create your account password',
  createPermit: 'Create Permit',
  createPermitDraft: 'Create Permit Draft',
  createPermitRole: 'Create Permit Role',
  createPermits: 'Create Permits',
  createRegister: 'Create Register',
  createRequestPermit: 'Sign & Request',
  createRequestTeamPermit: 'Request',
  createSchedule: 'Create Schedule',
  createSupportUser: 'Create Support User',
  createTask: 'Create Task',
  createTaskDraft: 'Create Task Draft',
  createTaskLinkedToObservationDescription:
    'Create a task linked to the Observation',
  createTemplate: 'Create Template',
  createZonesNow: 'Create Zones Now',
  createZonesNowDescription:
    'You do not need to set up all of your zones now, you can save progress and finish another time.',
  creatingNewProperty: 'New Property',
  creator: 'Creator',
  critical: 'Critical',
  crossStageReadOnly: 'Read-only',
  crossStageRuleNote:
    'Rule targets a question in a different stage. Edit it from that stage.',
  crossStageRulesTitle: 'Rules targeting other question sets (read-only)',
  CSVImport: 'CSV Import',
  csvImportTutorial: 'CSV Import Tutorial',
  csvTemplateDownloaded: 'CSV Template Downloaded',
  currentOrganisationFeatureFlags: 'Current Organisation Feature Flags',
  currentSession: 'Current Session',
  currentUser: 'Current User',
  currentVersion: 'Current Version',
  custom: 'Custom',
  customColours: 'Custom colours',
  customIntervalDays: 'Custom Interval (days)',
  daily: 'Daily',
  dashboard: 'Dashboard',
  date: 'Date',
  dateAndTime: 'Date & Time',
  dateCaptured: 'Date Captured',
  dateCreated: 'Date Created',
  dateGranularity: 'Date Granularity',
  dateRaised: 'Date Raised',
  dateRange: 'Date Range',
  datesSelected: 'Date(s) Selected',
  dateUploaded: 'Date Uploaded',
  day: 'Day',
  dayOfMonth: 'Day of Month',
  dayOfWeek: 'Day of Week',
  days: 'Days',
  daysShort: 'd',
  deactivate: 'Deactivate',
  deactivateCompany: 'Deactivate Company',
  deactivated: 'Deactivated',
  declareWorkComplete: 'Declare Work Complete',
  declareWorkCompleteParagraph:
    'Are you sure works are complete? If you proceed, your permit will no longer be open.',
  decline: 'Decline',
  declineBriefing: 'Decline Briefing',
  declineBriefingWarning:
    'If you decline your last remaining role, you will be removed from this permit.',
  declineTransfer: 'Decline Transfer',
  defaultApproval: 'Default Approval',
  defaultApproverConfirmationText:
    'I certify that I have inspected the area in which the works were carried out, that all tools and materials have been removed and that no risk exists from the works specified in this permit.&nbsp;',
  defaultDurationMinutes: 'Default Duration (minutes)',
  defaultErrorMessage: 'An unexpected error has occurred',
  defaultFilter: 'Default Filter',
  defaultRequestorConfirmationText:
    'By signing this I certify that I have inspected the area in which the works were carried out, that all tools and materials have been removed and that no risk exists from the works specified in this permit and the permit is now closed.',
  defaultTaskTemplateHelperText:
    'Selected task template will automatically be added to Projects alongside the inspection template if not already present',
  defaultValue: 'Default Value',
  defaultValues: 'Default Values',
  defaultView: 'Default View',
  delete: 'Delete',
  deleteApprovalMessage: 'Are you sure you want to delete this approval?',
  deleteCheck: 'Delete Check',
  deleteChecklistConfirmation:
    'Are you sure you want to remove this checklist?',
  deleteCompany: 'Delete Company',
  deleteCompanyError: 'Delete Company Error',
  deleteComplianceRequirement: 'Delete compliance requirement',
  deleteComplianceRequirementConfirm: (title: string) =>
    `Are you sure you want to delete '${title}'? Existing company assignments for this requirement will continue their lifecycle.`,
  deleted: 'Deleted',
  deleteDigest: 'Delete Digest',
  deleteDigestConfirm: (referenceObjectType: string) =>
    `Are you sure you want to delete this ${referenceObjectType} digest? Its schedule will be cancelled and this cannot be undone.`,
  deletedPermitTemplateVersion: 'Permit Version Deleted',
  deleteDraft: 'Delete Draft',
  deleteDraftMessage: 'Are you sure you want to delete this draft?',
  deleteFileConfirmation: 'Delete File Confirmation',
  deleteFileMessage: 'Are you sure you want to delete this file?',
  deleteFolder: 'Delete Folder',
  deleteFolderConfirmation: 'Delete Folder Confirmation',
  deleteFolderMessage: 'Are you sure you want to delete this folder?',
  deleteGroupPermissionMessage:
    'Are you sure you want to remove this permission?',
  deleteLayer: 'Delete Layer',
  deleteNotificationConfirmation:
    'Are you sure you want to delete this notification?',
  deletePlan: 'Delete Plan',
  deletePlanMessage: 'Are you sure you want to delete this plan?',
  deleteProcessTagMessage: 'Are you sure you want to delete this tag?',
  deletePrompt: 'DELETE',
  deleteRole: 'Delete Role',
  deleteRule: 'Delete Rule',
  deleteTask: 'Delete Task',
  deleteTaskConfirmation: (taskName: string) =>
    `Are you sure you want to delete ${` "${taskName}"` || 'this task'}?`,
  deleteTemplate: 'Delete Template',
  deleteTemplateConfirmation:
    'Are you sure you want to delete this template? This action cannot be undone.',
  deleteThisRuleMessage:
    'This rule will be permanently removed. Do you want to continue?',
  deleteThisRuleQuestion: 'Delete this rule?',
  deleteValidation: 'Delete Validation',
  deleteValidationMessage: 'Are you sure you want to delete this validation?',
  deleteVersion: 'Delete Version',
  deleteView: 'Delete View',
  deleteWarningConfirmation: 'Are you sure you want to delete this warning?',
  deleteZone: 'Delete Zone',
  deleteZoneConfirmationMessage:
    'If you delete this zone, this action cannot be undone.',
  deleteZoneConfirmationNo: "No, I'll keep it",
  deleteZoneConfirmationYesDelete: 'Yes, delete',
  delimiter: 'Delimiter',
  describeYourObservation: 'Describe your observation',
  description: 'Description',
  descriptionAlreadySetOnProperty: (name: string) =>
    `Description is already set on "${name}".`,
  descriptionIsRequired: 'Description is required',
  deselectAll: 'Deselect All',
  destinationOrganisation: 'Destination Organisation',
  details: 'Details',
  detailsAndUsers: 'Details and Users',
  detailsAreBeingProcessedInTheBackground:
    'Details are being processed in the background',
  developer: 'Developer',
  diagram: 'Diagram',
  digestCreated: 'Digest Created',
  digestDayOfMonthRequired:
    'Select a day of the month, or report on the last day of the month',
  digestDayOfWeekRequired: 'Select a day of the week',
  digestDeleted: 'Digest Deleted',
  digestImmutableHint:
    'Digests cannot be edited. To change a cadence, delete the digest and create a new one.',
  digestReportsOn: 'Reports On',
  digests: 'Digests',
  digestScheduleDaily: (time: string) => `Daily at ${time}`,
  digestScheduleFallback: (frequency: string, time: string) =>
    `${frequency} at ${time}`,
  digestScheduleMonthlyDay: (dayOfMonth: number, time: string) =>
    `Monthly on day ${dayOfMonth} at ${time}`,
  digestScheduleMonthlyLastDay: (time: string) =>
    `Monthly on the last day at ${time}`,
  digestScheduleWeekly: (dayOfWeek: string, time: string) =>
    `Weekly on ${dayOfWeek} at ${time}`,
  digestsDescription:
    'Recurring emails summarising everything of one type raised in the last day, week or month.',
  digestTimeUtcHint: (utcTime: string) => `(${utcTime} UTC)`,
  digitalSignature: 'Digital signature',
  digitalSignatureInfo:
    'A Signature is required to Issue Permits. Once added it will be saved to your Profile.',
  disable: 'Disable',
  disabled: 'Disabled',
  disabledOrganisationMember: 'Deactivated Organisation Member',
  disabledProjectMember: 'Deactivated Project Member',
  disableLocationHierarchy: 'Disable Location Hierarchy',
  disableUser: 'Disable User',
  disableUserOrganisationWarning:
    'This will disable the user within the whole Organisation.',
  disableUserWarning: 'This will disable the user within this Project ONLY.',
  discard: 'Discard',
  discardBriefingSession: 'Discarding Briefing Session',
  discardBriefingSessionConfirmation:
    'Are you sure you want to discard incomplete briefing session?',
  discardChanges: 'Discard Changes',
  discardChangesMapGeoDrawingMessage:
    'You have unsaved areas. Do you want to discard or save them?',
  discardDraft: 'Discard Draft',
  discardDraftConfirmation:
    'Do you really want to discard this draft? This action cannot be undone.',
  discarded: 'Discarded',
  discardedChecklistVersion: 'Checklist Version Discarded',
  discardExtensionRequest: 'Discard Extension Request',
  discardWorkConfirmation:
    'This will remove it from your device. This action cannot be undone.',
  disclaimer: 'Disclaimer',
  disclaimerActions: 'Disclaimer Actions',
  disclaimerConfiguration: 'Disclaimer Configuration',
  disclaimerDescription:
    "This is the message that will show to a <span class='font-weight-bold'>{0}</span> once the work is complete.",
  dismiss: 'Dismiss',
  distanceToPermit: 'Distance to Permit',
  distanceToPointOfWork: 'Distance to Point of Work',
  document: 'Document',
  documentAcceptance: 'Document Acceptance (Pass/Fail)',
  documentAcknowledged: 'Document Acknowledged',
  documentAdded: 'Document added',
  documentAlreadyReviewed: 'This document has already been reviewed',
  documentAppraisalForm: 'Document Appraisal Form',
  documentConfiguration: 'Document Configuration',
  documentEngineTemplate: 'Document Template',
  documentEngineTemplates: 'Document Templates',
  documentExpiryDate: 'Document Expiry Date',
  documentIntelligence: 'Document Intelligence',
  documentName: 'Document name',
  documentOptional: 'Document Optional',
  documentProof: 'Document Proof',
  documentProperties: 'Document Properties',
  documentQuestionsReviewedWithCompliance: (
    reviewed: number,
    total: number,
    passed: number,
  ) =>
    `This document has ${reviewed}/${total} questions reviewed with a compliance score of ${passed}/${total}`,
  documentRequired: 'Document Required',
  documents: 'Documents',
  documentsAndPhotos: 'Documents and photos',
  documentSignOffPassFail: 'Document Sign Off (Pass/Fail)',
  documentsIntegration: 'Documents Integration',
  documentsOnly: 'Documents only',
  documentType: 'Document Type',
  doesNotRequireApprovalDescription:
    'Does not require Approval. If you want to require approval please go to the settings tab.',
  doItLater: 'Do it Later',
  domains: 'Domains',
  done: 'Done',
  dontAddNewColumnsOrEditColumnHeaders:
    "Don't add new columns or edit column headers.",
  doPeriodicCheck: 'Do New Check',
  doPreStartTasks: 'Do Pre-Start Tasks',
  download: 'Download',
  downloadAll: 'Download All',
  downloadAppraisalReceiptOnSubmission:
    'Download Appraisal Receipt on Submission',
  downloadChecklist: 'Download Checklist',
  downloadComplete: 'Download Complete',
  downloadCSV: 'Download CSV',
  downloadCsvTemplate: 'Download CSV Template',
  downloadDocument: 'Download Document',
  downloadFailed: 'Download failed',
  downloading: 'Downloading',
  downloadingChecklist: 'Downloading Checklist',
  downloadingDocument: 'Downloading Document',
  downloadingInspection: 'Downloading Inspection',
  downloadingPermit: 'Downloading Permit',
  downloadingReport: 'Downloading Report',
  downloadingTask: 'Downloading Task',
  downloadInspection: 'Download Inspection',
  downloadLayerFile: 'Download Layer File',
  downloadPdf: 'Download PDF',
  downloadPermit: 'Download Permit',
  downloadSampleFile: 'Download sample file',
  downloadTask: 'Download Task',
  draft: 'Draft',
  draftCreated: 'Draft Created',
  draftDeleted: 'Draft Deleted',
  draftDiscarded: 'Draft Discarded',
  draftIsOutdated: 'Draft is Outdated',
  draftIsOutdatedDescription:
    'This draft is not the latest version of the template and cannot be submitted',
  draftName: 'Draft Name',
  draftNote: 'Draft Note',
  draftNoteTitle:
    'You can add a note to this draft to make it easier to identify.',
  drafts: 'Drafts',
  draftVersionCreated: 'Draft Version Created',
  dragCsvFileToUpload: 'Drag CSV file to upload',
  dragToResize: 'Drag to resize',
  dragToResizeClickToCollapse: 'Drag to resize · Click to collapse',
  draw: 'Draw',
  drawMaxBounds: 'Draw Max Bounds',
  drawNewArea: 'Draw New Area',
  drawOnMap: 'Draw on Map',
  drawOnMapRequiredErrorMessage: 'Please draw your location on the map',
  dropAPin: 'Drop A Pin',
  due: 'Due',
  dueDate: 'Due Date',
  dueDateHelperDaily:
    'First round of this inspection will be due at this date.',
  dueDateHelperOnce: 'The inspection must be completed by this date.',
  dueDateHelperRecurring: 'First inspection round will be due at this date.',
  dueDateLatestFirst: 'Due date (latest first)',
  dueDateMustBeTodayOrInTheFuture: 'Due date must be today or in the future',
  dueDateRequired: 'Due date is required',
  dueDateSoonestFirst: 'Due date (soonest first)',
  dueTime: 'Due Time',
  duplicateCompanyDetected: 'Potential Duplicate Company Detected',
  duplicateCompanyIfOneOfThese: (action: string) =>
    `If it's one of these companies, click <span class="font-weight-bold">${action}</span>.`,
  duplicateCompanySureNewCompany: (action: string) =>
    `If you're sure it's a new company, click <span class="font-weight-bold">${action}</span>.`,
  duplicateCompanyWarning: "We've found similar companies that already exist:",
  duplicatedImageDetected: 'Duplicated Image Detected',
  duplicatedImageDetectedModalDescription:
    'We’ve detected that this image had been used before on other permits listed below',
  duplicateImagesDetected: 'Duplicate Images Detected',
  duplicateItem: 'Duplicate Item',
  duplicateQuestion: 'Duplicate Question',
  duplicates: 'Duplicates',
  duplicateZoneDescription: 'Zone name is duplicated',
  duplicateZoneModalDescription:
    'You have 2 or more zones with the same name, zone labels should be unique. Please edit this before saving and exiting.',
  duplicateZones: 'Duplicate Zones',
  duplicatingArchivedItemWillCreateNewLiveItem:
    'Duplicating this archived item will create a new live item.',
  duration: 'Duration',
  durationMinutes: 'Duration (Minutes)',
  dxfTileset: 'DXF Tileset',
  dynamicNoSearchResults: (searchedItem: string) =>
    `Could not find any ${searchedItem} against the search query`,
  easting: 'Easting',
  edit: 'Edit',
  editAnnotation: 'Edit Annotation',
  editAnnotations: 'Edit Annotations',
  editAnswer: 'Edit Answer',
  editApproval: 'Edit Approval',
  editAttachments: 'Edit Attachments',
  editBoundary: 'Edit Boundary',
  editBusinessUnit: 'Edit Business Unit',
  editCompany: 'Edit Company',
  editCompanyPermissions: 'Edit Company Permissions',
  editCompanyProfile: 'Edit Company Profile',
  editComplianceRequirement: 'Edit Compliance Requirement',
  editConditions: 'Edit Conditions',
  editDocument: 'Edit Document',
  editDueDate: 'Edit Due Date',
  editFile: 'Edit File',
  editFinalSignoff: 'Edit Final Signoff',
  editFolder: 'Edit Folder',
  editInMap: 'Edit in Map',
  editInspection: 'Edit Inspection',
  editItem: 'Edit Item',
  editKioskSettings: 'Edit Kiosk Settings',
  editLayerName: 'Edit Layer Name',
  editMember: 'Edit Member',
  editNotification: 'Edit Notification',
  editObservation: 'Edit Observation',
  editObservationDescription: 'Change the Observation details',
  editOrDiscardThisBriefingSession: 'Edit or discard this briefing session',
  editOrganisation: 'Edit Organisation',
  editOrganisationPermissions: 'Edit Organisation Permissions',
  editParticipant: 'Edit Participant',
  editParticipants: 'Edit Participants',
  editPermission: 'Edit Permission',
  editPermissions: 'Edit Permissions',
  editPermitTemplate: 'Edit Permit Template',
  editPlan: 'Edit Plan',
  editProfile: 'Edit Profile',
  editProjectPermissions: 'Edit Project Permissions',
  editProperties: 'Edit Properties',
  editQueries: 'Edit Queries',
  editQuestions: 'Edit Questions',
  editRegister: 'Edit Register',
  editResponse: 'Edit Response',
  editRole: 'Edit Role',
  editRule: 'Edit Rule',
  editTag: 'Edit Tag',
  editTask: 'Edit Task',
  editTaskDescription: 'Change the tasks properties',
  editTemplateName: 'Edit Template Name',
  editTemplates: 'Edit Templates',
  editThisZone: 'Edit this Zone',
  editViewName: 'Edit View Name',
  editWarning: 'Edit Warning',
  editYourInformation: 'Edit your personal profile information',
  ellipse: 'Ellipse',
  email: 'Email',
  emailAddress: 'Email',
  emailAddresses: 'Email Addresses',
  emailAddressesRequired: 'At least one email address is required',
  emailAlreadyAdded: 'This email is already added',
  emailIsRequired: 'Email address is required',
  emailMustBeValid: 'Email must be valid',
  emailNotificationSent: 'Email Notification Sent',
  emailRecipientsPDFOnlyAccess:
    'People without a SiteAssist account will only get access to the PDF version.',
  emptyFolder: 'This folder is empty',
  emptyFolderMessage:
    'You can upload documents or photos by tapping on the plus button in the top right corner',
  emptyQuestionsMessage:
    'You cannot activate this version without <span class="text-bold">{0} Questions</span>. Please add questions to the relevant section{1} before activating this version.',
  emptyString: '(empty string)',
  enable: 'Enable',
  enableCheckInNoRoleWarning:
    'Check in is enabled but no role can check themselves or others in. Add "Check Self In / Out" or "Check Others In / Out" to a role.',
  enableCheckInOnTemplate: 'Enable check in / check out on this template',
  enableCheckInSelfBypassesControllerWarning:
    'A role can check other participants in, but another role can check themselves in. Self check-in bypasses the controller; remove "Check Self In / Out" from that role if entry must be controlled.',
  enableChildPermit: 'Enable Child Permit',
  enabled: 'Enabled',
  enableDiagramDescription:
    'Enable diagrams to allow showing and manipulating register items on a plan or document.',
  enableDiagrams: 'Enable diagrams',
  enableDocumentSelectionFromProcore: 'Enable document selection from Procore',
  enableDuration: 'Enabling this will enforce a maximum duration for Permits.',
  enableKioskMode: 'Enable Kiosk Mode',
  enableLive: 'Enable (Live)',
  enableLocationHierarchy: 'Enable Location Hierarchy',
  enablePermitRequestExtension: 'Enable permit request extension',
  enablePermitRequestMaximumDuration: 'Permit request maximum duration?',
  enablePermitShorten: 'Enable shorten permit',
  enablePermitSuspension: 'Enable permit suspension',
  enablePermitTransfer: 'Enable permit holder transfer',
  enableProjectLocations: 'Enable Project Locations',
  enableSignupForm: 'Enable sign up Form',
  end: 'End',
  endDate: 'End Date',
  endDateAndTime: 'End date & time',
  ended: 'Ended',
  ends: 'Ends',
  endsOnAscending: 'Ends (oldest first)',
  endsOnDescending: 'Ends (newest first)',
  endTime: 'End Time',
  ensureMaxDuration:
    'Please create permits at least 30 minutes into the future from the start time',
  enterAddress: 'Enter address',
  enterAnswer: 'Enter Answer',
  enterBasicGroupInfo: 'Enter some basic information about the group',
  enterCity: 'Enter City',
  enterCompany: 'Enter Company',
  enterDocumentName: 'Enter Document Name',
  enterEditMode: 'Enter Edit Mode',
  enterEmail: 'Enter Email',
  enterFirstName: 'Enter First Name',
  enterLastName: 'Enter Last Name',
  enterName: 'Enter Name',
  enterNewEmail: 'Enter a new email address',
  enterNote: 'Enter Note',
  enterNumber: 'Enter Number',
  enterPhone: 'Enter Phone',
  enterPhoneNumber: 'Enter phone number',
  enterPostcode: 'Enter postcode',
  enterReason: 'Enter Reason',
  enterRequestChanges:
    'E.g. Please provide clearer image of the signage for question 1',
  enterTime: 'Enter Time',
  errorMessage: 'Error Message',
  errorMessage400: 'Request failed with status code 400',
  errorMessage401: 'Request failed with status code 401',
  errorMessage404: 'Not Found',
  errorMessage500: 'Request failed with status code 500',
  errorPlayingVideo: 'Error playing video',
  errorPleaseTryAgain: 'Error (please try again)',
  errorProcessingRequest: 'Error processing request',
  errorProcessingVideo: 'Error processing video',
  errorUploading: 'Error uploading',
  errorUploadingFileWithStatus: (fileName: string, status: number) =>
    `Error uploading ${fileName} (${status}). Please try again.`,
  everyNDays: (days: number) => `Every ${days} days`,
  evidenceFromDocument: 'Evidence from Document',
  example: 'Example',
  excludeWeekends: 'Exclude Weekends',
  excludingWeekends: 'excluding weekends',
  exit: 'Exit',
  exitEditMode: 'Exit Edit Mode',
  exitWithoutSaving: 'Exit without saving?',
  expectedCheckOut: (time: string) => `Expected out ${time}`,
  expectedCheckOutTime: 'Expected check-out time',
  expectedEnd: 'Expected End',
  expectedEndLatestFirst: 'Expected end, latest first',
  expectedEndSoonestFirst: 'Expected end, soonest first',
  expiration: 'Expiration',
  expired: 'Expired',
  expiredComplianceRequirement: (count: number) =>
    `${count} Expired Compliance Requirement${count > 1 ? 's' : ''}`,
  expires: 'Expires',
  expiresIn: 'Expires In',
  expiryDate: 'Expiry Date',
  expiryDateMustBeInFuture: 'Expiry date must be in the future',
  expiryDateRequired: 'Expiry date is required',
  expiryDurationRequired:
    'Standard validity is required when expiry is enabled',
  expiryEnabled: 'Expiry enabled',
  expiryEnabledDescription:
    'Whether this document has an expiry date that the approver sets on review.',
  export: 'Export',
  exportAsPdfDescription: 'Export as a PDF file',
  exportProperties: 'Export Properties',
  exportQuestions: 'Export Questions',
  exports: 'Exports',
  exportSuccessMessage: 'Success! Click Download to download the export.',
  extend: 'Extend',
  extendCheckIn: 'Extend Check In',
  extendedDate: 'Extended Date',
  extendEndTimeError: 'Please choose a time after the end time of the permit',
  extendPermit: 'Extend Permit',
  extendRejectDescription:
    'If you want to reject the extension please provide a reason.',
  extendStandardWording:
    'By extending your check-in session, you confirm your presence on site and agree to follow the associated risk assessments and method statements. Choose your new expected check-out time.',
  extensionApproved: 'Extension Approved',
  extensionInformation: 'Extension Information',
  extensionReason: 'Extension Reason',
  extensionRejected: 'Extension Rejected',
  extensionRequestCancelled: 'Extension Request Cancelled',
  extensionRequestDisclaimer:
    'By signing this you acknowledge your responsibility as the current Permit Holder and agree to approved the extension request.',
  extensionRequested: 'Extension Requested',
  extensionRequestedDetails: 'Extension Request Details',
  extensionsRequested: 'Extension Requested',
  extensionsReviewTitle: 'Who can review Extensions?',
  external: 'External',
  externalCode: 'External Code',
  externalUser: 'External User',
  fail: 'Fail',
  failed: 'Failed',
  failedToCapturePhoto: 'Failed to capture photo',
  failedToSaveIntegrationSettings: 'Failed to save integration settings',
  failedToUpdateProperties: 'Failed to update properties',
  featureFlagEnabledForAllProjects: (feature: string) =>
    `${feature} is enabled for all projects at organisation level, so it is on for this project whatever the setting below.`,
  featureFlagNotEnabledForAllProjects: (feature: string) =>
    `${feature} is not enabled for all projects at organisation level, so the setting below decides.`,
  featureFlags: 'Feature Flags',
  featureFlagsUpdated: 'Feature Flags Updated',
  feedbackSubmitted: 'Feedback submitted',
  fetchingOrganisationUser: 'fetching user details...',
  fetchingProjectUser: 'fetching user details...',
  file: 'File',
  fileArchived: 'File archived',
  fileDownloaded: 'File downloaded',
  fileFormatInvalid: 'File Format Invalid',
  filePath: 'File Path',
  filePreview: 'File Preview',
  fileReadAccess: 'File Read access',
  fileReadAccessMessageProof:
    'SiteAssist needs file read access to upload proofs',
  fileReadAccessMessageSignature:
    'SiteAssist needs file read access to save your signature',
  fileReadError: 'Failed to read file. Please try again.',
  fileRenamed: 'File renamed',
  fileRestored: 'File restored',
  fileSize: 'This file is too big',
  fileWriteAccess: 'File write access',
  fileWriteAccessMessage:
    'SiteAssist needs file write access to save your signature',
  fillOutAllQuestions: 'Please fill out all questions',
  filter: 'Filter',
  filterPermits: 'Filter Permits',
  filterRegisters: 'Filter Registers',
  filters: 'Filters',
  filtersApplied: 'Filters Applied',
  filterValue: 'Filter Value',
  finalSignOff: 'Final Sign Off',
  finalSignoff: 'Final Signoff',
  finalSignOffChecks: 'Final Sign Off Checks',
  finalSignOffDisclaimerDescription:
    "This is the message that will show to the <span class='font-weight-bold'>Approver</span> once the work is complete.",
  finalSignOffDisclaimerText:
    'By signing this I certify that I have inspected the area in which the works were carried out, that all tools and materials have been removed and that no risk exists from the works specified in this permit and the permit is now closed.',
  finalSignOffDisclaimerTitle: 'Final Sign Off Disclaimer',
  finalSignOffLabel: 'Final SignOff Label',
  finalSignOffProgressText: (doneReviews: number, allReviews: number) =>
    `${doneReviews} out of ${allReviews} - View Final Sign Offs`,
  finalSignOffQuestions: 'Final SignOff Questions',
  finalSignOffRequested: 'Final sign-off requested',
  finalSignOffs: 'Final SignOffs',
  finalSignoffSubtitle:
    'If your template needs questions to be reviewed by an auth group before, please select.',
  finished: 'Finished',
  finishReview: 'Finish Review',
  fireWatchDescription: (name: string) =>
    `A ${name} Countdown must be completed at the end of this permit.`,
  fireWatchTitle: 'There is a {0} minute {1} countdown after expiry time',
  firstDueDate: 'First Due Date',
  firstDueDateMustBeTodayOrInTheFuture:
    'First due date must be today or in the future',
  firstDueDateRequired: 'First due date is required',
  firstName: 'First Name',
  firstNameRequired: 'First name is a required field',
  fixer: 'Fixer',
  flagTrail: 'Flag Trail',
  folder: 'Folder',
  folderCreated: 'Folder created',
  folderInformation: 'Folder Information',
  folderMoved: 'Folder moved',
  folderPermissionsMissing: 'Required file access permissions missing',
  folderRenamed: 'Folder renamed',
  followTheseStepsToMergeInBulk: 'Follow these steps to merge in bulk.',
  for: 'For',
  forceCheckedOut: 'Force Checked Out',
  forceCheckOut: 'Force Check Out',
  forceCheckOutDescription: (name: string) =>
    `${name} will be checked out without their signature. This is recorded against you with the reason below.`,
  forceCheckOutReason: 'Reason for forcing the check-out',
  forceExpire: 'Force Expire',
  forceExpireCompetencyConfirmation: 'This will expire this competency',
  forceExpireComplianceConfirmation: 'This will expire this compliance',
  forgotPassword: 'Forgot Password',
  forgotPasswordLinkSent: 'Forget Password Link Sent',
  formDescription: 'Form Description',
  formDescriptionPlaceholder:
    'Please complete the form below to access the platform',
  formProperties: 'Form Properties',
  formSubmitted: 'Form Submitted',
  formula: 'Formula',
  formulasRecalculated: 'Formulas Recalculated',
  formUrl: 'Form URL',
  forThisRegisterNewItemsNeedToBeCreatedInTheDiagramView:
    'For this register, new items need to be created in the diagram view.',
  frequency: 'Frequency',
  fromCameraOrGallery: 'from camera or gallery',
  fullScreen: 'Full Screen',
  gatewayTimeoutError: 'API is unreachable right now',
  gdprConsentModalBody: `
<p>
  At <strong>SiteAssist</strong>, we are committed to protecting your personal data.
</p>
<p>
  To continue using our services, please review and consent to how we process your data
  in accordance with our
  <a href="https://siteassist.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
</p>

<p>We collect and process your data to:</p>

<ul>
  <li>Provide and improve our services</li>
  <li>Offer personalized content and recommendations</li>
  <li>Ensure security and prevent fraud</li>
  <li>Comply with legal obligations</li>
</ul>
`,
  gdprConsentModalTitle: 'Your Privacy Matters to Us',
  general: 'General',
  generalApproval: 'General Approval',
  generalInformation: 'General information',
  generalInformationPlaceholder:
    'Enter any useful information about this project',
  generalPermissions: 'General Permissions',
  generalSettings: 'General Settings',
  generatePermitPDF: 'Download Permit PDF',
  generatePermitPDFDescription: 'Export this permit as a PDF file',
  generateTaskPdfDescription: 'Export this task as a PDF file',
  generating: 'Generating...',
  generatingPermitPDF: 'Generating Permit PDF',
  genericError: 'Error processing request',
  genericProjectPlanError:
    'Something went wrong while initalizing the project plan',
  geoLocation: 'Geolocation',
  geoLocationInputType: 'What is the geolocation input type?',
  getTotalDrawnAreaSize: (value: string, unit: string) =>
    `Total drawn area size: ${value} ${unit}`,
  globalSettings: 'Global Settings',
  globalWarnings: 'Global Warnings',
  goBack: 'Go Back',
  goToDetails: 'Go To Details',
  groupCreated: 'Group Created',
  groupCreatedSuccessfully: 'Group created successfully',
  groupDeleted: 'Group Deleted',
  groupDetails: 'Group Details',
  groupName: 'Group Name',
  groups: 'Groups',
  groupSkipped: '(Group Skipped)',
  hasLinkedParentPermit: 'Has a linked parent permit',
  hasNotBeenDone: 'Has not been done',
  hasOverdueInspections: 'Has overdue inspections',
  hasPermitLinks: 'Has Permit Links',
  hazardDetected: 'Hazard Detected',
  heicFileNotSupported:
    'HEIC images are not supported. Please convert to JPEG or PNG before uploading.',
  help: 'Help',
  here: 'here',
  hi: 'Hi',
  hide: 'Hide',
  hideAll: 'Hide All',
  hideAllLayers: 'Hide All Layers',
  hideBriefingText: 'Hide Briefing Text',
  hideGroupIfAnswer: 'Hide Group if answer is:',
  hideIfAnswerIs: 'Hide if answer is',
  hideInfo: 'Hide Info',
  hideLocation: 'Hide Location',
  hideMenu: 'Hide Menu',
  hidePhotos: 'Hide Photos',
  hideSignature: 'Hide Signature',
  hideTestProjects: 'Hide Test Projects',
  hideTranscript: 'Hide Transcript',
  high: 'High',
  highlightDetectedObjects: 'Highlight Detected Objects',
  home: 'Home',
  homePageLoadingStep1: 'Loading application modules...',
  homePageLoadingStep2: 'Setting up user...',
  homePageLoadingStep3: 'Establishing secure connections...',
  homePageLoadingStep4: 'Finalizing setup...',
  hour: 'Hour',
  hours: 'Hours',
  hoursRemaining: (n: number) => `${n} hour${n === 1 ? '' : 's'} remaining`,
  hoursShort: 'h',
  howToGuide: '`How` to guide',
  identifierProperty: 'Identifier Property',
  identifierPropertyDescription:
    'Choose which property is used to identify items on the plan (usually a name).',
  identityProviderDeletionConfirmation:
    'Identity Provider Deletion Confirmation',
  ifAnswer: 'If answer',
  ifYouExitWithoutSaving:
    'If you exit without saving you will lose your zones. Save now?',
  ifYouThinkThisIsAnError:
    'If you think this is an error, please contact help@siteassist.co.uk',
  image: 'Image',
  imageLocation: 'Image Location',
  imageMetadata: 'Image Metadata',
  imageObjectDetection: 'Image Object Detection',
  impersonated: 'Impersonated',
  impersonatedUserFailed: 'Failed to impersonate user',
  impersonatedUserSuccess: 'Impersonated user successfully',
  impersonateUser: 'Impersonate User',
  import: 'Import',
  important: 'Important!',
  importFromArcGisCsv: 'Import from ArcGIS CSV',
  importFromArcGisCsvError: (message: string) =>
    `Could not import area from CSV: ${message}`,
  importFromArcGisCsvPrompt:
    'Upload an ArcGIS coordinate vertices CSV (with Easting/Northing columns) to import the area.',
  importFromArcGisCsvSuccess: (count: number) =>
    `Imported ${count} area(s) from CSV`,
  importProperties: 'Import Properties',
  importQuestions: 'Import Questions',
  importViaCsv: 'Import via CSV',
  inactive: 'Inactive',
  inactiveScheduleInspections: 'Inactive Inspections',
  includeDocumentAppraisal: 'Include document appraisal',
  incompleteBriefingSession: 'Incomplete Briefing Session',
  incorrectUsernameOrPassword:
    'Incorrect username or password. Please check your credentials and try again.',
  incrementalId: 'Incremental ID',
  incrementByParent: 'Increment by Parent',
  independentApproval: 'Independent Approval',
  independentApprovalDescription:
    'An Independent Approval means that this approval has to be done by someone other than the permit author, permit holder or any other approver.',
  info: 'Info',
  infoText: 'Info Text',
  initiated: 'Initiated',
  initiatedBy: 'Permit Author',
  initiateTransfer: 'Initiate Transfer',
  initiatorRole: 'Initiator Role',
  initiatorRoleQuestion: 'Who starts the process?',
  initiatorRoleText:
    'If your template needs questions to be reviewed by an auth group before, please select.',
  inProgress: 'In Progress',
  inProgressFormDetected: 'In-progress form detected',
  inProgressFormDetectedDescription:
    'You have an in-progress form. Would you like to continue or discard it?',
  inReview: 'In Review',
  insertLabel: 'Insert Label',
  inspection: 'Inspection',
  inspectionChecklist: 'Inspection Checklist',
  inspectionCompleted: 'Inspection Completed',
  inspectionCreatedNotification: 'Inspection assigned to you',
  inspectionDetails: 'Inspection Details',
  inspectionDone: 'Inspection Done',
  inspectionDownloadComplete: 'Inspection Download Complete',
  inspectionDownloadFailed: 'Inspection Download Failed',
  inspectionDraftSaved: 'Inspection draft saved',
  inspectionDue: 'Inspection Due',
  inspectionExiryDate: 'Inspection Expiry Date',
  inspectionExpired: 'Inspection Expired',
  inspectionInformation: 'Inspection Information',
  inspectionItems: 'Inspection Items',
  inspectionOverdueNotification: 'Inspection overdue',
  inspectionPermissions: 'Inspection Permissions',
  inspections: 'Inspections',
  inspectionScheduling: 'Inspection Scheduling',
  inspectionsFilter: 'Inspections Filter',
  inspectionsNotConfiguredInProject:
    'Inspections have not been configured for this project. A Project Admin can add Inspection Checklist Templates to the project',
  inspectionTaskTemplate: 'Inspection Task Template',
  inspectionTemplate: 'Inspection Template',
  inspectionTemplates: 'Inspection Templates',
  inspectionType: 'Inspection Type',
  inspectionTypeAToZ: 'Inspection type (A-Z)',
  inspectionTypeZToA: 'Inspection type (Z-A)',
  inspectionUpdatedNotification: 'Inspection updated',
  installMobileApp: 'Install Mobile App',
  insufficientStorage: 'Insufficient storage',
  integrations: 'Integrations',
  integrationSettingsSaved: 'Integration settings saved successfully',
  intendedRecepient: 'Intended recepient',
  intendedRecipient: 'Intended Recipient',
  introText: 'Intro Text',
  intuneProtectedFileNotSupported:
    'This file is encrypted by a Microsoft Intune policy and cannot be uploaded. Open it in the app it came from (for example Outlook or OneDrive), save or share an unprotected copy, then upload that.',
  invalidAnswer: 'Invalid Answer',
  invalidAnswerDescription: 'Invalid Answer Description',
  invalidAnswerExplanation:
    'Sorry, this Permit Request cannot be processed if the answer to this question is',
  invalidAnswerOption: 'Enter An Invalid Answer Option',
  invalidEmailAddress: 'Invalid email address',
  invalidImageFile:
    'Invalid image file. Please upload a valid JPEG or PNG image.',
  invalidMfaCodeValidity: 'MFA code must be between 5 and 60 minutes',
  invalidPdfFile: 'Invalid PDF file. Please upload a valid PDF document.',
  invalidPermissions: 'Invalid Permissions',
  invalidTokenValidity:
    'Token validity must be between 60 and 525600 minutes (1 year)',
  invalidW3W: 'Invalid what3words address',
  invitationResent: 'Invitation resent',
  invite: 'Invite',
  inviteAndAddAnother: 'invite and add another',
  inviteNewUser: 'Invite New User',
  inviteSent: 'Invite sent',
  inviteUser: 'Invite User',
  inviteUserToOrganisation: 'Invite User to Organisation',
  inviteUserToProject: 'Invite User to Project',
  ios: 'IOS',
  isChecklistOwnedByRegister: 'Is checklist owned by a register item?',
  isDue: 'Is due',
  isDueInLessThan: 'Is due in less than',
  isInspection: 'Is Inspection',
  isNotPresent: 'Is Not Present',
  isObservation: 'Is Observation',
  isPresent: 'Is Present',
  issue: 'Issue',
  issued: 'Issued',
  issuedBy: 'Issued by',
  issueDescription: 'Issue the permit to begin work on site',
  issueNewPermit: 'Issue a new permit',
  issuePermit: 'Issue Permit',
  issuesDetected: 'Issues Detected',
  itemAdded: 'Item Added',
  itemArchived: 'Item Archived',
  itemCreated: 'Item Created',
  itemCreatedViaCsv: 'Item Created via CSV',
  itemDetails: 'Item Details',
  itemLinkedToChecklist: 'Item linked to checklist',
  itemLinkedToPermit: 'Item linked to permit',
  itemPermissions: 'Item Permissions',
  items: 'Items',
  itemsSelected: (selectedItems: number, totalItems: number) =>
    `${selectedItems} of ${totalItems} Items selected`,
  itemsSelectedCount: (selectedItems: number) =>
    `${selectedItems} item${selectedItems === 1 ? '' : 's'} selected`,
  itemTransferredToDiagram: 'Item transferred to diagram',
  itemTypes: 'Item Types',
  itemUnarchived: 'Item Unarchived',
  itemUpdated: 'Item Updated',
  itemUpdatedViaCsv: 'Item Updated via CSV',
  Kafka: 'Kafka',
  KAZPOC: 'KMZ POC',
  keepRecordingMinimumSeconds: (seconds: number) =>
    `Keep recording - ${seconds}s minimum`,
  kiosk: 'Kiosk',
  km2: 'km²',
  label: 'Label',
  Labelling: 'Labelling',
  labels: 'Labels',
  lastActive: 'Last active',
  lastBriefed: 'Last Briefed',
  lastCheck: 'Last Check',
  lastCompleted: 'Completed',
  lastDayOfMonth: 'Last Day of Month',
  lastModified: 'Last Modified',
  lastName: 'Last Name',
  lastNameRequired: 'Last name is a required field',
  lastRun: 'Last Run',
  lastSevenDays: 'Last 7 days',
  lastSixMonths: 'Last 6 months',
  lastThirtyDays: 'Last 30 days',
  lastTwoWeeks: 'Last 2 weeks',
  lastUpdated: 'Last Updated',
  lat: 'Lat',
  latLongFormatted: (lat: number, long: number) =>
    `Lat/Long: ${lat.toFixed(3)}/${long.toFixed(3)}`,
  layerMetadata: 'Layer metadata',
  leastCompletedInspections: 'Least completed inspections',
  leastOpenTasks: 'Least open tasks',
  leastPlannedInspections: 'Least planned inspections',
  leave: 'Leave',
  leaveWithoutSavingData: 'If you leave, any collected data will not be saved.',
  leaveWithoutSavingParticipants:
    'If you leave, any added participants will not be saved.',

  level: 'Level',
  link: 'Link',
  linkCreated: 'Link created',
  linkedDocuments: 'Linked Documents',
  linkedOrganisations: 'Linked Organisations',
  linkedPermit: 'Linked Permit',
  linkedPermits: 'Linked Permits',
  linkedToPermit: (templateName: string | undefined, shortUUID: string) =>
    `Parent Permit: ${templateName} — ${shortUUID}`,
  linkedToRegister: 'Linked to a register',
  linkedToTitle: (templateName: string | undefined, shortUUID: string) =>
    templateName
      ? `Linked to: ${templateName} — ${shortUUID}`
      : `Linked to: ${shortUUID}`,
  linkedUsersDeletetionError:
    'There are users linked to this company. To proceed, please merge the users into another company before deletion.',
  linkExpiry: 'Link expires in 60 minutes',
  list: 'List',
  live: 'Live',
  liveTemplateDescription:
    'Making this template live will allow this template to be used in permit requests.',
  loading: 'Loading',
  location: 'Location',
  locationAccuracy: 'Location Accuracy',
  locationArchiveConfirmationText: `By archiving, you will no longer be able to edit. However, you can
  still view (read-only) archived items by selecting 'Archived' in the
  Status Filter.`,
  locationArchived: 'Location Archived',
  locationBoundaryAvailableTooltip:
    'This location has a boundary that can be viewed on the map',
  locationCheckIn: 'Location Check In',
  locationDisableConfirmationMessage:
    'Disabling Location Hierarchy for this project will hide these changes. This will disable visibility of Location Hierarchy for all those working on this project.',
  locationEnableConfirmationMessage:
    'Enabling Location Hierarchy for this project will push these changes to live. This will enable all those working on this project to see these changes.',
  locationError: 'Location Error',
  locationErrorDescription:
    'Location is not available. Please check if device location services are enabled.',
  locationHierarchyDisabled: 'Location Hierarchy Disabled',
  locationHierarchyEnabled: 'Location Hierarchy Enabled',
  locationLoading: 'Location Loading ...',
  locationNotUpdatedRecently: 'Location not updated recently, please try again',
  locationOfWorks: 'Location of Works',
  locationOptions: 'Location Options',
  locationPlaceholder: 'Location ...',
  locationRenamed: 'Location Renamed',
  locationsHierarchy: 'Locations Hierarchy',
  locationsSaved: 'Locations Saved',
  lockedAfterCreationField: 'Locked after creation Field',
  loggedInAs: 'Logged in as',
  loggingIn: 'Logging in',
  login: 'Log in',
  loginScreenPromo: 'Total Control of High-Risk Work',
  logo: 'Logo',
  logoOptional: 'Logo (Optional)',
  logout: 'Logout',
  logoutMessage: 'You will be returned to the login screen.',
  long: 'Long',
  low: 'Low',
  m2: 'm²',
  mainContact: 'Main Contact',
  maintenance: 'Maintenance',
  maintenanceDescription:
    'We are currently under maintenance, we will be back soon',
  manageAllPermitTemplates: 'Manage All Permit Templates',
  manageGis: 'Manage GIS',
  manageMembers: 'Manage Members',
  managePermitProjectConfiguration: 'Manage Permit Project Configuration',
  manageProjects: 'Manage Projects',
  manuallyScheduledPerItem: 'Manually scheduled per item',
  map: 'Map',
  mapBoxStyleUrl: 'MapBox Style URL',
  MapboxWsmTest: 'Mapbox WSM test',
  mapCenterLatitude: 'Map Center Latitude',
  mapCenterLongitude: 'Map Center Longitude',
  mapLayerCouldNotBeDisplayed: (layerName: string) =>
    `The layer "${layerName}" could not be displayed`,
  mapLayers: 'Map Layers',
  mappingConfigurationSaved: 'Mapping Configuration Saved',
  mapPOC: 'Map POC',
  mapZoom: 'Map Zoom',
  markAllAsRead: 'Mark all as read',
  max: 'Max',
  max20Letters: 'Max 20 letters',
  maxBoundsPolygonCannotBeEdited:
    'The max bounds polygon can’t be edited. Please remove it and draw a new one.',
  maxDurationIncludingExtensions: 'Max duration including extensions?',
  maxDurationInfo:
    'This means the permit end date must be within this timeframe.',
  maxExpiryDurationDays: 'Standard validity (days)',
  maxExpiryDurationDaysDescription:
    'The standard validity period in days — shown to the approver as guidance when setting the expiry date.',
  maxFileSizeExceeded: 'File size is above the limit of {0}mb',
  maximumAttachmentsReached: 'Maximum attachments reached',
  maximumDuration: 'Maximum Duration',
  maximumExpiryTime: 'Maximum Expiry Time',
  maximumRowsCSVExportError: 'Maximum of 50,000 rows can be downloaded',
  maxSize15mb: 'Max size 15mb',
  media: 'Media',
  medium: 'Medium',
  memberAdded: 'Member added',
  memberRemoved: 'Member removed',
  members: 'Members',
  membersUpdated: 'Members Updated',
  mergeAppraisalResults: 'Merge appraisal results with original document',
  mergeCompanies: 'Merge Companies',
  mergeCompany: 'Merge Company',
  mergeIntoCompany: 'Merge Into Company',
  metricName: 'Metric Name',
  mfaActiveSessions: (count: number) =>
    `${count} active session${count !== 1 ? 's' : ''}`,
  mfaActiveSessionsTab: 'Active MFA Sessions',
  mfaAllowSkip: 'Allow Skip MFA',
  mfaAllowSkipDescription:
    'Temporarily allow users to skip Multi-Factor Authentication for a specified period',
  mfaAutoVerifyCloseMessage: 'This page can be closed at any time',
  mfaAutoVerifyFailed: 'Verification Failed!',
  mfaAutoVerifyFailedMessage:
    'Please check your email for the verification code and enter it manually',
  mfaAutoVerifying: 'Please wait while we verify your code...',
  mfaAutoVerifyingDescription: 'Verifying your code automatically...',
  mfaAutoVerifySuccess: 'Verification Successful!',
  mfaAutoVerifySuccessMessage:
    "Your multi-factor authentication has been successfully verified. You're all set and can now safely close this page.",
  mfaCanNotAccessEmail: 'Can’t access your email?',
  mfaCodeExpired: 'Code has expired. Please request a new one.',
  mfaCodeExpiresIn: (timeRemaining: string) =>
    `Code expires in ${timeRemaining}`,
  mfaCodeMustBe6Digits: 'Code must be 6 digits long',
  mfaCodeMustBe6Numbers: 'Code must be exactly 6 numbers',
  mfaCodeMustBeOnlyNumbers: 'Code must be only numbers',
  mfaCodeSentToEmail: 'MFA code sent to email',
  mfaCodeValidity: 'MFA Code Validity',
  mfaCodeVerified: 'MFA code verified',
  mfaConfiguration: 'MFA Configuration',
  mfaEnable: 'Enable MFA',
  mfaEnableDescription:
    'Enforce Multi-Factor Authentication for all users in the organisation.',
  mfaEnforceMfaFrom: 'Enforce MFA From',
  mfaEnforceMfaFromDescription:
    'Date when MFA will become required for all users',
  mfaEnforceMfaFromIsRequired: 'Enforce on is required',
  mfaEnterCodeDescription: (email: string) =>
    `Enter the 6-digit code sent to ${email}`,
  mfaErrorSendingCode: 'Error sending MFA code',
  mfaFailedToLoadSessions: 'Failed to load MFA sessions',
  mfaLoadingSessions: 'Loading MFA sessions...',
  mfaNeedToSignInWithDifferentAccount:
    'Need to sign in with a different account?',
  mfaNeedToSkipMfaPrefix: 'You can skip this check until ',
  mfaNeedToSkipMfaSuffix: ' After that it will be required for all accounts.',
  mfaNoActiveSessionsFound: 'No active MFA sessions found',
  mfaNoActiveSessionsFoundDescription:
    "You don't have any active multi-factor authentication sessions.",
  mfaNoActiveSessionsFoundDescriptionSupport:
    'This user doesn’t have any active multi-factor authentication sessions. Either they have not enabled MFA or they have not logged in with MFA enabled.',
  mfaRevokeSession: 'Revoke MFA Session',
  mfaRevokeSessionDescription:
    'Are you sure you want to revoke this MFA session?',
  mfaSendAnotherCode: 'Send me another code',
  mfaSessionRevokedSuccessfully: 'MFA session revoked successfully',
  mfaSessionValidity: 'Session Validity',
  mfaSessionValidityDescription:
    'The user will have to re-verify after this duration',
  mfaSessionValidityIsRequired: 'Session validity is required',
  mfaSkipConfiguration: 'MFA Skip Configuration',
  mfaSkipDuration: 'Skipped Session Validity',
  mfaSkipDurationDescription:
    'If skipped, the user will have to re-verify after this duration',
  mfaSkipDurationIsRequired: 'Skip duration is required',
  mfaSkipForNow: 'Skip for now',
  mfaSkipForSsoUsers: 'Skip for SSO Users',
  mfaSkipForSsoUsersDescription:
    'Users logging in via Single Sign-On will not be required to use MFA.',
  mfaSkippedSuccessfully: 'MFA validation skipped successfully',
  mfaSkipUntil: (date: string) => `You can skip the MFA check until ${date}`,
  mfaSkipUntilDescription: (time: string) =>
    `We will ask you to do this check again in ${time} if you skip.`,
  mfaSkipUntilDescription2: (time: string) =>
    `If you validate the code from your email we will only ask you to revalidate in ${time}.`,
  mfaTitle: 'Multi-Factor Authentication (MFA)',
  mfaToken: 'MFA Token',
  mfaValidateFromEmail: 'Validate from email',
  mfaVerification: 'Verification',
  mfaVerificationRequired: 'MFA Verification Required',
  mfaVerifyCode: 'Verify Code',
  mfaVerifying: 'Verifying...',
  mfaWrongVerificationCode: 'Wrong verification code',
  min: 'Min',
  minimise: 'Minimise',
  minOneDay: 'Must be at least 1 day',
  minsShort: 'm',
  minute: 'Minute',
  minutes: 'Minutes',
  missing: 'missing',
  missingSomeAdditionalInputs: 'Missing some additional inputs',
  missingToken: 'Missing token',
  modified: 'Modified',
  monthly: 'Monthly',
  monthsShort: 'm',
  more: 'More',
  moreActions: 'More Actions',
  moreOptions: 'More Options',
  mostCompletedInspections: 'Most completed inspections',
  mostOpenTasks: 'Most open tasks',
  mostPlannedInspections: 'Most planned inspections',
  mostRecentCheckIn: 'Most recent check-in',
  move: 'Move',
  moved: 'Moved',
  movedChecklistTemplate: 'Checklist Template Moved',
  movedChecklistVersionToDraft: 'Checklist Version Moved to Draft',
  moveDown: 'Move Down',
  movedPermitTemplate: 'Moved Permit Template',
  movedPermitTemplateVersionToDraft: 'Permit Version Moved to Draft',
  moveFolder: 'Move Folder',
  moveTemplate: 'Move Template',
  moveTheMapAndAddPointsToDrawAZone:
    'Move the map and add points to draw a zone',
  moveTheMapAndPressButtonWhenHappyWithMaxBounds:
    "Move the map and press the button when you're happy with the max bounds of the camera",
  moveTo: 'Move To',
  moveToNextStepDescription: 'Move task to the next step in the workflow',
  moveUp: 'Move Up',
  multipleFinalSignoffs: 'Multiple Final Sign-Offs',
  multipleLocations: 'Multiple Locations',
  multipleProjects: 'Multiple Projects',
  multipleSelected: 'Multiple selected',
  multipleStatuses: 'Multiple Statuses',
  multipleTemplates: 'Multiple Templates',
  mustBeAtLeast1Day: 'Must be at least 1 day',
  mustBeAtMost365Days: 'Must be at most 365 days',
  mustBeCompletedOnMobile: 'Must be completed on mobile device.',
  mustBePartOfProjectChecklistCreate:
    'You must be added to a project prior to creating a checklist. Please contact your project admin to be added to the project.',
  mustBePartOfProjectPermitCreate:
    'You must be added to a project prior to creating a permit. Please contact your project admin to be added to the project.',
  mustUploadProof: 'You must upload a proof for this question',
  mustUploadSignature: 'You must upload your signature for this question',
  myChecklists: 'My Checklists',
  myInspections: 'My Inspections',
  myPermits: 'My Permits',
  myProjects: 'My Projects',
  myRole: 'My Role',
  myTasks: 'My Tasks',
  myWork: 'My Work',
  myWorkEmptySubtitle: 'Work you save to finish later will show up here.',
  myWorkEmptyTitle: 'Nothing in progress',
  myWorkSubmitting: 'Submitting…',
  myWorkWaitingToSubmit: 'Waiting to submit',
  na: 'N/A',
  name: 'Name',
  nameAToZ: 'Name (A-Z)',
  nameIsRequired: 'Name is required',
  nameOfGroup: 'Name of the group',
  nameOfProject: 'Name of the project',
  nameZToA: 'Name (Z-A)',
  needsAttention: 'Needs Attention',
  networkError: 'Network error. Please check your connection and try again.',
  never: 'Never',
  new: 'New',
  newApproval: 'New Approval',
  newChecklist: 'New Checklist',
  newComment: 'New Comment',

  newCompaniesRequireApproval: 'Do new companies require approval?',
  newCompanyAdded: 'New Company Added',
  newComplianceRequirement: 'New Compliance Requirement',
  newDigest: 'New Digest',
  newEndDateAndTime: 'New End Date & Time',
  newEndTimeFrom: 'New end time from:',
  newestFirst: 'Newest First',
  newFilter: 'New Filter',
  newFinalSignoff: 'New Final Signoff',
  newFolder: 'New Folder',
  newInspection: 'New Inspection',
  newLocation: 'New Location',
  newNavigation: 'New Navigation',
  newParticipants: 'New Participants',
  newPassword: 'New password',
  newPeriodicCheck: 'New periodic check',
  newPermissions: 'New Permissions',
  newPermit: 'New Permit',

  newQuestion: 'New Question',
  newResponse: 'New Response',
  newRole: 'New Role',
  newStep: 'New Step',
  newTag: 'New Tag',
  newTask: 'New Task',
  newTemplate: 'New Template',
  newUser: 'New User',
  newUserInvitedToProjectPermissionsInfo:
    'If the user needs additional permissions, please ask your Project Admin or get in touch with us',
  newValidation: 'New Validation',
  newView: 'New View',
  next: 'Next',
  nextDue: 'Next Due',
  nextInspectionDue: 'Inspection Due',
  nextNominatedApprover: 'Next Nominated Approver',
  no: 'No',
  noAccessToProjectMessage:
    'You are not a member of this project.\nPlease speak to someone to add you to the project.',
  noAnswersAvailable: 'No answers available',
  noAuthorisedPersonsFound:
    '<p>No Permit Approvers found in this project. Try <a class="text-link">adding Permit Approvers</a> to your project, before adding them to a group</p>',
  noAvailableActions: 'No available actions',
  noAvailablePropertyComponents: 'No available property components',
  noBriefingRolesAvailable:
    'No roles are available for briefing on this permit template. A project authorisation group needs the "Be briefed as" permission for a role of this template.',
  noBusinessUnitsFound: 'Could not find any business units',
  noCameraAvailable: 'No camera available',
  noChangesDetected: 'No changes detected',
  noChartsAvailableYet: 'No charts are available for this page yet',
  noCheckedInUsersMatchFilters: 'No checked-in users match the current filters',
  noChecklists: 'No checklists were found',
  noChecklistsFound: 'No checklists found',
  noChecklistsFoundProject:
    '<p>No checklists found for this project. Try <a class="text-link">adding Checklist Templates</a> to your project, before adding them to a group</p>',
  noChecklistText: 'No checklists found.',
  noChecksSubmitted: 'No checks submitted to date',
  noColours: 'No colours',
  noComplianceAssigned:
    'No compliance requirements have been assigned to this company.',
  noComplianceRequirementsYet:
    'No compliance requirements yet. Add one to get started.',
  noDataForSelectedPeriod: 'No data for the selected period',
  noDateSelected: 'No date selected',
  noDescription: 'No description',
  noDigestsYet: 'No digests yet',
  noDraftInspectionsFound: 'No draft inspections found',
  noDraftsFound: 'No drafts found',
  noEligibleRolesForBriefingFound: 'No eligible roles for briefing found',
  noEndDateSelected: 'No end date selected',
  noEvents: 'There are no events for this permit yet',
  noExit: 'No, exit',
  noFilesFound: 'No files found',
  noFilter: 'No Filter',
  noImDone: "No, I'm done",
  noInspectionsRaisedInProject:
    'No inspections have been raised in this project yet',
  noInspectionTemplates: 'No inspection templates',
  noIssuesDetected: 'No issues detected',
  noItemSelected: 'No item selected',
  noItemsFound: 'No items found',
  noLayersAddedToDisplay: 'No layers added to display.',
  noLinkedPermitsFound: 'No linked permits found',
  noMetadataAvailable: 'No metadata available',
  noMoreResults: 'No more results.',
  noMoreTemplates: 'No more templates available',
  noMultipleChoiceQuestions: 'No multiple choice questions',
  none: 'None',
  noNotifications: 'No notifications',
  noNotificationsEnabled:
    'No notifications enabled yet. Add a notification rule to allow or automate alerts.',
  noObservations: 'No observations were found',
  noObservationsToReview: 'No observations to review',
  noObservationsYet: 'No observations yet',
  noOfInspectionsHaveExpired: (noOfExpiredInspections: number) =>
    `${noOfExpiredInspections}${
      noOfExpiredInspections > 1
        ? ' Inspections have expired'
        : ' Inspection has expired'
    }`,
  noOfOtherPropertiesHidden: (n: number) => `${n} other properties hidden ...`,
  noOfOverdueInspections: (count: number = 0) =>
    `${count} Overdue ${count > 1 ? 'Inspections' : 'Inspection'}`,
  noOneCheckedIn: 'No one is currently checked in',
  noOneOnSite: 'No one is currently on site',
  noOptions: 'No Options',
  noOptionsAddedYet: 'No options added yet',
  noOptionsFound: 'No options found',
  noOrganisationDescription:
    'You are not part of any organisations. This could be because you have been removed by an administrator. Please speak to us if you think a mistake was made.',
  noOtherParticipants: 'There are no other participants',
  noParticipantsFound: 'No participants found',
  noParticipantsToCheckIn: 'No briefed participants are available to check in',
  noParticipantsToCheckOut: 'No one is currently checked in',
  noParticipantTimelineEntries: 'Nothing to show for this participant',
  noPeriodicChecksText: "You don't have any Periodic Checks configured",
  noPermissionProjectModifyAuthGroups: (groupNames: string) =>
    `Only people in the following groups can edit: ${groupNames}`,
  noPermissions: 'No Permissions',
  noPermissionsSet: 'No permissions set',
  noPermissionToRequestChanges: 'No permission to request changes',
  noPermissionToSubmitStep: 'No permission to submit step',
  noPermitRoleText: 'No permit roles found.',
  noPermits: 'No permits were found',
  noPermitsFound:
    '<p>No permits found for this project. Try <a class="text-link">adding Permit Templates</a> to your project, before adding them to a group</p>',
  noPermitTypesAvailable: '“No permit types available',
  noPlannedInspections: 'No planned inspections',
  noPlans: 'No Plans',
  noPlansDescription:
    'No technical plans have been uploaded yet. To upload a new plan, first create a location (via Locations Hierarchy) and upload a plan for that location.',
  noPlansDescription2:
    "View our 'How to' guide for more help, or go to Locations hierarchy now to upload a plan.",
  noPreStartTasksFound: 'You don’t have any tasks configured.',
  noPreStartTaskText: 'No pre-start tasks found.',
  noPreviewAvailable:
    'No preview available for this file type. Download it to view.',
  noProjectsDashboard:
    'You must be added to a live project to view the organisation dashboard. Please contact your Project Admin to be added to a project. If you are part of test projects you can see their dashboard in the project page.',
  noProjectsWithProjectAdminRole:
    'You are not a Project Admin in any Projects.',
  noProjectToAddToTemplateText:
    'No additional projects to add to this template',
  noProofsProvided: 'No proofs provided',
  noPropertiesCreatedYet:
    'No properties created yet. Click the + button to add a property.',
  noPropertiesSelected: 'No properties selected',
  noPropertyHasBeenAddedYet: 'No property has been added yet',
  noQueriesConfigured: 'No queries configured',
  noQueryText: 'No query text entered',
  noQuestionsText: 'No questions found.',
  noReasonProvided: 'No reason provided',
  noRegistersFound: 'No registers found',
  noResourceFoundToGenerateAnExport: (typeLocalized: String) =>
    `Error while trying to export ${typeLocalized}. Try again in a few moments`,
  noResultsFound: 'No results were found',
  noResultsFoundPleaseChangeSearch:
    'No results were found, please change search',
  noRolesConfigured: 'No roles configured',
  northing: 'Northing',
  noRuleText: 'There is no rules associated with this question.',
  noSearchResults: 'No results found',
  noSignatureSet: 'No signature set',
  noSiteAssistAccount: 'No SiteAssist account',
  noStartDateSelected: 'No start date selected',
  noSubfolders: 'No subfolders here',
  noSubfoldersMessage:
    'You can still move it into this folder by tapping the button below',
  noTagsFound: 'No tags found',
  notApplicable: 'N/A',
  noTasks: 'No tasks were found',
  noTasksFound: 'There are no outstanding tasks',
  noTasksLinkedYet: 'No tasks linked yet.',
  noTaskTemplatesAvailableDescription:
    'No task templates available.\nContact your project admin.',
  noTaskTemplateSelectedText:
    'When no template is selected, user will choose from their available templates when answering this question.',
  noTaskTemplatesSelected: 'No task templates selected',
  notAssigned: 'Not Assigned',
  note: 'Note',
  noTemplatesFound: 'No templates found',
  notFound: 'Not Found',
  noThanks: 'No, thanks',
  nothingSelected: 'Nothing selected',
  notification: 'Notification',
  notificationDeleted: 'Notification deleted',
  notificationEmailAddress: 'Notification Email Address',
  notificationEmailAddressInfo:
    'Notifications to individual approvers will no longer be sent, when a permit is raised the notification will go to this email address',
  notifications: 'Notifications',
  notificationSettingsUpdated: 'Notification settings updated',
  notificationsSettings: 'Notifications Settings',
  notifiedApprovers: 'Notified Approvers',
  notify: 'Notify',
  notifyAll: 'Notify All',
  notifyApprovers: 'Notify Approvers',
  notifyApproversText:
    'Choose who should be notified about this approval request.',
  notifyApproversTextChecklist:
    'Choose who should be notified about this checklist.',
  notifyOnCompanyAdminAddedDescription:
    'These addresses are emailed whenever a user is granted the Company Admin role in this organisation.',
  notifyOthers: 'Notify Others',
  notifyOthersInstruction:
    'Select people or add email addresses that should get notified.',
  noTimeSelected: 'No time selected',
  notPinned: 'Not Pinned',
  notProcessed: 'Not Processed',
  notRequired: 'Not Required',
  notSelected: 'Not selected',
  notSigned: 'Not Signed',
  notSynced: 'Not Synced',
  notUploaded: 'Not Uploaded',
  noUnreadNotifications: 'No unread notifications',
  noUpdatesAvailable: 'No updates available',
  noUpdatesAvailableDescription:
    'You are currently using the latest version of the app',
  noUserFound: 'No user found',
  noUsersFound: 'No users found',
  noVersions: 'There are no versions for this Template',
  noVersionsFound: 'No versions found',
  noVisibilityRulesText:
    'No visibility rules configured. This question will always be visible.',
  now: 'Now',
  noZone: 'No Zone',
  noZonesSelected: 'No Zones Selected',
  nTasksOutstanding: (n: number) =>
    `${n} Task${n === 1 ? '' : 's'} Outstanding`,
  number: 'Number',
  numberOfAnswers: 'Number of Answers',
  numberOfDays: 'No. of days',
  numberOfQuestions: (n: number) => `${n} question${n !== 1 ? 's' : ''}`,
  numberOfTotalItemsSelected: (number: number, total: number) =>
    `${number} of ${total} Item${total > 1 ? 's' : ''} selected`,
  NumberShort: 'No.',
  numChecklists: (n: number) => `${n} Checklist${n === 1 ? '' : 's'}`,
  numeric: 'Numeric',
  numLinkedDocuments: (n: number) =>
    `${n} Linked Document${n === 1 ? '' : 's'}`,
  numTasks: (n: number) => `${n} Task${n === 1 ? '' : 's'}`,
  objectDetectionWarningModalDescription:
    'We’ve detected that this image contains a potential hazard',
  observation: 'Observation',
  observationActions: 'Observation Actions',
  observationAnalytics: 'Observation Analytics',
  observationAnalyticsDescription:
    'Live analytics on observations, risk levels and corrective tasks for this project',
  observationAssignedNotification: 'Observation has been assigned a task',
  observationClosed: 'Observation Closed',
  observationClosedNotification: 'Observation has been closed',
  observationDetails: 'Observation Details',
  observationEditedSuccessfully: 'Observation edited successfully',
  observationReadyForTriage: 'Observation ready for triage',
  observationRecordAudioHint:
    "Start the audio recording, say what you see and what you've done",
  observationRecordPhotoHint: 'Take photos of the evidence',
  observationRecordVideoHint:
    'Start the video recording, say what you see and what you have done',
  observationRejected: 'Observation Rejected',
  observationRejectedNotification: 'Observation has been rejected',
  observations: 'Observations',
  observationsEnabledForAllProjects: 'Observations for All Projects',
  observationSettingsDescription:
    'How observations can be raised from this template.',
  observationsNotSupportedOnWeb: "Observations aren't supported on web yet",
  observationsNotSupportedOnWebDescription:
    'Open this observation in the SiteAssist mobile app to view and manage it.',
  observationSubmitted: 'Observation Submitted',
  observationSubmittedDescription:
    'Your safety report has been successfully submitted and is being reviewed by our team',
  ok: 'OK',
  okay: 'Okay',
  oldestFirst: 'Oldest First',
  oldPermissions: 'Old Permissions',
  onboarding: 'Onboarding',
  onboardingForm: 'Onboarding Form',
  once: 'Once',
  onceSubmissionApproved:
    'Once your submission has been approved, you must login to complete the necessary details.',
  onlyFromCamera: 'only from camera',
  onlyNSignaturesCollected: (n: number, total: number) =>
    `Only ${n} out of ${total} signatures collected. Do you want to proceed with the submission and address the pending signature later?`,
  onlyOneParentCanBeSelected:
    'Only one parent can be selected. The selected permit will be considered the parent of this permit.',
  onlyPdfAllowed: 'Only PDF files are accepted',
  onSite: 'Checked in',
  onSiteSummary: (onSite: number, total: number) =>
    `${onSite}/${total} checked in`,
  open: 'Open',
  openAllComments: 'Open All Comments',
  openAllQuestionComments: 'Open All Question Comments',
  openChecklist: 'Open Checklist',
  opened: 'Opened',
  openInApp: 'Open in App',
  openInAppMessage: (type: string) =>
    `Would you like to open this ${type.toLowerCase()} in the SiteAssist mobile app for a better experience?`,
  openInBrowser: 'Open in Browser',
  openInNewTab: 'Open in new tab',
  openInspection: 'Open Inspection',
  openItemInNewTab: 'Open Item in new tab',
  openLinkedItem: 'Open linked item',
  openMap: 'Open Map',
  openMobileApp: 'Open Mobile App',
  openObservation: 'Open Observation',
  openPermit: 'Open Permit',
  openShape: 'Open Shape Detected',
  openShapeModalDescription:
    'Please close the polygon shape before proceeding (by pressing the "tick icon")',
  openTask: 'Open Task',
  openTasks: 'Open Tasks',
  openTemplate: 'Open Template',
  openToRetry: 'loading failed. Retry',
  operative: 'Operative',
  operativeInfo:
    'Standard Users can create permits and checklists, and view project data',
  operativeTooltip:
    'Can request an individual permit using the mobile application as well as view all open permits related to them.',
  operator: 'Operator',
  option: 'Option',
  optional: 'Optional',
  optionalField: 'Optional Field',
  optionalProperty: 'Optional Property',
  optionalRequirements: 'Optional requirements',
  options: 'Options',
  or: 'or',
  orAddParticipantsManually: 'Or, Add Participants Manually',
  orderChanged: 'Order Changed',
  organisation: 'Organisation',
  organisationAuthenticationUpdated: 'Organisation Authentication Updated',
  organisationCreated: 'Organisation Created',
  organisationFeatureFlagUpdated: 'Organisation Feature Flag Updated',
  organisationLinkType: 'Link Type',
  organisationName: 'Organisation Name',
  organisationOverview: 'Organisation Overview',
  organisationPermissions: 'Organisation Permissions',
  organisations: 'Organisations',
  organisationSettings: 'Organisation Settings',
  organisationSettingsAndProfile: 'Organisation Settings & Profile',
  organisationTemplate: 'Organisation Template',
  organisationUpdatedSuccessfully: 'Organisation Updated Successfully',
  OrgSlashProject: 'Org/Project',
  otherParticipants: 'Other Participants',
  outcome: 'Outcome',
  outstandingPreStartTask: 'Outstanding Pre-Start Task(s)',
  overdue: 'Overdue',
  overdueBy: (duration: string) => `Overdue by ${duration}`,
  overdueCheckOuts: 'Overdue Check-Outs',
  overdueTasks: 'Overdue Tasks',
  override: 'Override',
  overrideFormulaValue: 'Override Formula Value',
  overrideRegisterItemFormulaWarning:
    'This will override the value calculated by this formula and LOCK it to the value you set.',
  overview: 'Overview',
  ownedByAnotherOrg: 'Owned by another organisation',
  ownedByOrg: (orgName: string) => `Owned by ${orgName}`,
  padToNumberOfCharacters: 'Pad to number of characters',
  page: 'Page',
  parentPermit: 'Parent Permit',
  parentPermitRequired: 'This permit must be linked to a parent permit',
  parentPermitRequiredNoEligibleAlert:
    'You need to raise a parent permit before being able to raise this permit.',
  participant: 'Participant',
  participantAdded: 'Participant added',
  participantBriefed: 'Participant Briefed',
  participantBriefedViaQRCode: 'Participant Briefed via QR Code',
  participantInformation: 'Participant Information',
  participantLog: 'Participant Log',
  participantRemoved: 'Participant Removed',
  participants: 'Participants',
  participantsAdded: 'Participants Added',
  participantsAddedAfterApproval:
    'These participants will be added to the permit after approval',
  participantsBriefed: 'Participants Briefed',
  participantsCheckedInBy: (name: string) => `Participants checked in: ${name}`,
  participantsCreated: 'Participants Created',
  participantSignature: 'Participant Signature',
  participantSigned: 'Participant Signed',
  pass: 'Pass',
  passed: 'Passed',
  password: 'Password',
  passwordConfirmationRequired: 'Confirm password is required',
  passwordRequired: 'Password is required',
  passwordResetSuccess: 'Password reset successfully!',
  passwordResetSuccessNextAction:
    'You can now log into the web and mobile apps with your new password',
  passwordsMustMatch: 'Passwords must match',
  pasteAnswers: 'Paste answers',
  pasteOptions: 'Paste Multiple Choice Options',
  pasteOptionsDescription:
    'Paste your options in the field below. Each option should be on a new line.',
  pasteOptionsPlaceholder: 'Paste options here',
  pdf: 'Pdf',
  PDFdemo: 'PDF Demo',
  pdfEditor: 'PDF Editor',
  pdfGenerationFailed: 'PDF failed to generate',
  pdfLink: 'PDF Link',
  pdfViewer: 'PDF Viewer',
  pending: 'Pending',
  pendingAcceptance: 'Pending Acceptance',
  pendingAction: 'Pending action',
  pendingSignatureYouHaveSigned: 'Pending, (You have signed)',
  people: 'People',
  peopleAndContractors: 'People & Contractors',
  performChecks: 'Perform Checks',
  performChecksDescription:
    'Do Post Permit Checks so that it can go to Final Sign-off',
  periodicCheck: 'Periodic Check',
  periodicCheckDeleted: 'Periodic Check Deleted',
  periodicChecks: 'Periodic Checks',
  periodicCheckSaved: 'Periodic Check Saved',
  periodicCheckSubmitted: 'Periodic Check Submitted',
  periodicCheckTitle: 'Periodic Check Title',
  permission: 'Permission',
  permissionLevel: 'Permission Level',
  permissions: 'Permissions',
  permissionsUpdated: 'Permissions Updated',
  permissionUpdate: 'Permission Update',
  permit: 'Permit',
  permitAcceptanceRequired: 'Permit Acceptance Required',
  permitActions: 'Permit Actions',
  permitApprove: 'Permit Approve',
  permitApproved: 'Permit approved',
  permitApprover: 'Permit Approver',
  permitApproverFilter: 'Permit Approver',
  permitApproverTooltip:
    'Can create an approver permits based on their Authorisation Groups',
  permitAreas: 'Permit Areas',
  permitAssignedToYou: 'Permit assigned to you',
  permitAuthorCompany: 'Permit Author Company',
  permitCancelled: 'Permit Cancelled',
  permitCancelledNotifications: 'Permit cancelled',
  permitChangesRequested: 'Permit changes are requested',
  permitCheckIn: 'Permit Check In',
  permitChecklists: 'Permit Checklists',
  permitClonable: 'Permit Clonable',
  permitCloned: 'Permit has been cloned',
  permitClosed: 'Permit Closed',
  permitClosureBlockedByChildren:
    'Permit closure blocked by linked child permits',
  permitContacts: 'Permit Contacts',
  permitDetails: 'Permit Details',
  permitDiscarded: 'Permit discarded',
  permitDoesntRequireApproval:
    'The permit will be automatically issued after the permit holder creates or receives it',
  permitDownloadComplete: 'Permit Download Complete',
  permitDownloadFailed: 'Permit Download Failed',
  permitDraftCreated: 'Permit Draft Created',
  permitDraftDeleted: 'Permit Draft Deleted',
  permitDraftSaved: 'Permit Draft Saved',
  permitDraftUpdated: 'Permit Draft Updated',
  permitDuration: 'Permit Duration',
  permitEnd: 'Permit End',
  permitExpiresAt: (endsOn: string, createdOn: string) => {
    const expiration = new Date(endsOn)
    const creation = new Date(createdOn)
    const time = expiration.toLocaleTimeString('en-US', {
      hour: '2-digit',
      hour12: false,
      minute: '2-digit',
    })

    const sameDay = expiration.toDateString() === creation.toDateString()
    if (!sameDay) {
      return `Permit expires tomorrow at ${time}`
    }

    const duration = moment.duration(expiration.getTime() - creation.getTime())
    const totalMinutes = Math.floor(duration.asMinutes())
    const hours = Math.round(totalMinutes / 60)

    if (hours <= 3 && hours >= 1) {
      return `${enBaseStrings.hoursRemaining(
        hours,
      )}: ${enBaseStrings.permitExpiresAtTime(time)}`
    }

    return `Permit expires today at ${time}`
  },
  permitExpiresAtTime: (time: string) => `Permit expires at ${time}`,
  permitExtendApproveDeclaration:
    'A request has been made to extend the permit to ',
  permitExtendApproveDeclaration1: 'A request has been made from ',
  permitExtendApproveDeclaration2: ' to extend the permit to ',
  permitExtended: 'Permit Extended',
  permitExtensionApprovedNotifications: 'Permit extension approved',
  permitExtensionCancelled: 'Permit Extension Cancelled',
  permitExtensionRejected: 'Permit Extension Rejected',
  permitExtensionRejectedNotifications: 'Permit extension rejected',
  permitExtensionRequested: 'Permit extension requested',
  permitHasExpired: 'Permit has expired',
  permitHolder: 'Permit Holder',
  permitHolderByDefault: 'Permit Holder (by default)',
  permitHolderChanged: 'Permit Holder Changed',
  permitHolderCompany: 'Permit Holder Company',
  permitHolderContactNumber: 'Permit Holder Contact Number',
  permitHolderFilter: 'Permit Holder',
  permitHolderQuestions: 'Permit Holder Questions',
  permitHolderSelfApprove: 'Permit holder self approve',
  permitHolderSelfSignoff: 'Permit holder self sign-off',
  permitHolderTel: 'Permit Holder Tel',
  permitHolderTooltip:
    'The Permit Holder will request a permit, and it will be issued after the Permit Approver or Project Admin reviews and approves the request.',
  permitInitiation: 'Permit Initiation',
  permitInitiationPermissionMessage:
    'When this permission is applied, a user must be in an Authorisation Group that has this permission enabled to raise the permit. Any Users not in one of these Auth Groups will not be able to raise this permit template.',
  permitInProgress: (entityType: string) => `${entityType} In-Progress`,
  permitIsNowClosed: 'Permit is now closed',
  permitIsNowOpen: 'Permit is now open',
  permitLink: 'Permit Link',
  permitLinkedToParent: 'Permit linked to parent',
  permitList: 'Permit List',
  permitMaximumDuration: 'Permit request maximum duration',
  permitMaximumDurationDays: (days: number) =>
    `This permit has a maximum duration of ${days} days`,
  permitMaximumDurationDhm: (days: number, hours: number, minutes: number) =>
    `This permit has a maximum duration of ${days} days, ${hours} hours and ${minutes} minutes`,
  permitNoPermission: 'You do not have permissions to view this permit',
  permitPDFGenerationFailed: 'Permit PDF failed to generate',
  permitPDFLinkReady: 'PDF Link Ready',
  permitPdfs: 'Permit Documents',
  permitPermissions: 'Permit Permissions',
  permitQRCodeDescription:
    'Scan this QR code with your phone camera to open the permit.',
  permitQuestions: 'Permit Questions',
  permitReceive: 'Permit Receive',
  permitReceived: 'Permit Received',
  permitRegister: 'Permit Register',
  permitRegisterDescription:
    'Download a .csv file of this project’s permit register over the selected date range.',
  permitRejected: 'Permit Rejected',
  permitRejectedNotifications: 'Permit rejected',
  permitRequested: 'Permit requested',
  permitRequiresApproval:
    'Before the permit can be opened, it will require approval by someone authorised',
  permitRequiresPostPermitChecks: 'Permit requires post-permit checks',
  permitResumed: 'Permit Resumed',
  permitResumedNotifications: 'Permit resumed',
  permitRole: 'Permit Role',
  permitRoleDeleted: 'Periodic Check Deleted',
  permits: 'Permits',
  permitShortened: 'Permit duration successfully shortened',
  permitSigned: 'Permit Signed',
  permitsNotConfiguredInProject:
    'Permits have not been configured for this project. A Project Admin can add Permit Templates to the project',
  permitStart: 'Permit Start',
  permitStatus: 'Permit Status',
  permitSummary: 'Permit Summary',
  permitSummaryLabel: 'Summary',
  permitSummaryPlaceholder: 'e.g. "Location - Works title"',
  permitSuspended: 'Permit Suspended',
  permitSuspendedNotifications: 'Permit suspended',
  permitSuspension: 'Permit Suspension',
  permitTemplate: 'Permit Template',
  permitTemplateAllowedChildrenUpdated: 'Allowed Child Permits Updated',
  permitTemplates: 'Permit Templates',
  permitTemplateVersions: 'Permit Template Versions',
  permitTimeline: 'Permit Timeline',
  permitToDig: 'Permit to Dig',
  permitTransfer: 'Permit Transfer',
  permitTransferAccepted: 'Permit Transfer Accepted',
  permitTransferApproved: 'Permit Transfer Approved',
  permitTransferApprovedNotifications: 'Permit transfer approved',
  permitTransferCancelled: 'Permit Transfer Cancelled',
  permitTransferCancelledNotifications: 'Permit transfer cancelled',
  permitTransferDeclined: 'Permit Transfer Declined',
  permitTransferInitiated: 'Permit Transfer Initiated',
  permitTransferInitiatedNotifications: 'Permit transfer initiated',
  permitTransferRejected: 'Permit Transfer Rejected',
  permitTransferRejectedNotifications: 'Permit transfer rejected',
  permitTransferRequestedNotifications: 'Permit transfer requested',
  permitType: 'Permit Type',
  permitTypes: 'Permit Types',
  permitTypesSelected: 'Permit Types Selected',
  permitUniqueCode: 'Permit Unique Code',
  permitWorkflow: 'Permit workflow',
  permitWorkflowDescription:
    'Configure the optional stages that this permit moves through during its lifecycle.',
  permitWorkflowFinalSignOff: 'Final Sign-Off',
  permitWorkflowFinalSignOffComplete: 'Complete Final Sign-Off',
  permitWorkflowFinalSignOffDescription:
    'This permit requires final sign-off to be closed',
  permitWorkflowPostPermitChecks: 'Post Permit Checks',
  permitWorkflowPostPermitChecksDescription:
    'The Permit Holder has to answer configured questions after work is complete.',
  permitWorkflowRequest: 'Request',
  permitWorkflowRequestDescription:
    'The Permit Holder has to answer configured questions in order to Request a Permit. Project Admins review the answers to questions and decide whether to issue or reject the Permit.',
  phoneRequired: 'Phone is a required field',
  photo: 'Photo',
  photos: 'Photos',
  photosOnly: 'Photos only',
  pin: 'Pin',
  pinItem: 'Pin Item',
  pinnedFilters: 'Pinned Filters',
  pinOnTheLeft: 'Pin on the left',
  pinOnTheRight: 'Pin on the right',
  plan: 'Plan',
  planDeleted: 'Plan Deleted',
  planName: 'Plan Name',
  planned: 'Planned',
  planPublished: 'Plan Published',
  plans: 'Plans',
  planSaved: 'Plan Saved',
  planUploaded: 'Plan Uploaded',
  planUploadFailed: 'Plan upload failed',

  planView: 'Plan View',
  pleaseAddAtLeastOneAttachment: 'Please add at least one attachment',
  pleaseCompleteAllQuestions: 'Please complete all questions before proceeding',
  pleaseDownloadApp: 'Please download the mobile app',
  pleaseEnterADescription: 'Please enter a description',
  pleaseEnterALocation: 'Please enter a location',
  pleaseEnterASummary: 'Please enter a summary',
  pleaseFillAllFieldsCorrrectly: 'Please fill all fields correctly',
  pleaseNote: 'Please Note',
  pleasePassDeviceTo: 'Please pass the device to:',
  pleaseProvideNameForThisLevel: 'Please provide a name for this level.',
  pleaseProvideNameForThisLocation: 'Please provide a name for this location.',
  pleaseRefreshPageAndTryFileDownloadAgain:
    'File failed to download. Please refresh the page and try again.',
  pleaseRemoveAndTryUploadingPlanAgain:
    'Plan failed to upload, please remove and try uploading the plan again',
  pleaseSeachByNameOrEmail: 'Please search by name or email',
  pleaseSearchByTemplateName: 'Please search by template name',
  pleaseSelect: 'Please Select',
  pleaseSelectALocation: 'Please select a location',
  pleaseSelectAnArea: 'Please select an area',
  pleaseSelectAnAssignee: 'Please select an assignee',
  pleaseSelectAnEndDate: 'Please select an end date',
  pleaseSelectAnExpiryTime: 'Please select an expiry time',
  pleaseSelectAnInspection: 'Please select an inspection',
  pleaseSelectAProject: 'Please select a project',
  pleaseSelectAssignee: 'Please select an assignee',
  pleaseSelectAStartDate: 'Please select a start date',
  pleaseSelectATemplate: 'Please select a template',
  pleaseSelectDateAtLeast30MinutesAfterStart:
    'Please select a date at least 30 minutes after the start date',
  pleaseSelectDateRange: 'Please select a date range',
  pleaseSelectPriority: 'Please select a priority',
  pleaseSelectStep: 'Please select a step',
  pleaseSelectTheExpiryTime: 'Please select the expiry time',
  pleaseSelectThePointOfWorks: 'Please select the point of works',
  pleaseSetTheExpiryTimeOfTheInspection:
    'Please set the Expiry time of the inspection',
  pleaseSpeakToSiteAssistMappingConfiguration:
    'Please speak to someone from SiteAssist to enable maps for your Project',
  pleaseUpdate: 'Please update the app to continue using SiteAssist.',
  pointOfWorks: 'Point of Works',
  pointOfWorks3Words: 'Point of Works What3Words',
  pointOfWorks3WordsPlaceholder: "eg. '///snow.logic.chains'",
  polygon: 'Polygon',
  polygonCoordinates: 'Polygon coordinates (British National Grid)',
  populateTemplateWithCompanyData: 'Populate template with company data',
  postcode: 'Postcode',
  postPermitChecks: 'Post Permit Checks',
  postPermitChecksComplete: 'Post Permit Checks Complete',
  postPermitChecksCountdown: 'Post permit checks countdown',
  postPermitChecksDescription:
    'Post Permit Checks must be done to close the permit.',
  postPermitChecksDisclaimerDescription:
    "This is the message that will show to the <span class='font-weight-bold'>Permit Holder</span> once the work is complete.",
  postPermitChecksDisclaimerForRequesterTitle:
    'Post Permit Checks Disclaimer for Requester',
  postPermitChecksDisclaimerText:
    'I certify that I have inspected the area in which the works were carried out, that all tools and materials have been removed and that no risk exists from the works specified in this permit.',
  postPermitChecksDisclaimerTitle: 'Post Permit Checks Disclaimer',
  postPermitChecksGeolocationWebTooltip:
    'Post Permit Checks for this permit template must be completed on a mobile device',
  postPermitChecksInfo: 'A participant should perform post permit checks',
  postPermitChecksRequest: 'Post Permit Checks Request',
  postPermitCheckTimeRemainingInfo:
    'Time remaining before the permit holder can perform post permit checks',
  postPermitCountdownFinished: 'Post Permit Checks Countdown Finished',
  postPermitQuestions: 'Post Permit Questions',
  prefix: 'Prefix',
  preparePermit: 'Prepare Permit',
  preparing: 'Preparing',
  present: 'present',
  preStart: 'Pre Start',
  preStartTaskConditionalLogicDescription:
    'Set the conditions that will trigger this Pre-Start Task',
  preStartTaskDescription: (readOnly: boolean) =>
    `${
      readOnly ? 'Tasks' : 'Set the tasks'
    } that must be completed for the Permit to start`,
  preStartTaskMustBeCompletedBeforeWork:
    'Task(s) must be completed before the permit is open',
  preStartTaskNoRuleText:
    'There is no rules associated with this Pre-Start Task.',
  preStartTasks: 'Pre-Start Tasks',
  preStartTasksRemaining: (n: number) =>
    n === 1 ? '1 task remaining' : `${n} tasks remaining`,
  preStartTaskStageChangeWarning:
    'Question set change will remove all rules. Are you sure you want to continue?',
  preStartTaskSubDescription:
    'The produce operates based on the order of the list below.',
  preventDeleteQuestionWithRuleText:
    'This question has rule(s) associated with it. Please remove it from all rules before you proceed.',
  preview: 'Preview',
  previewCardsOnlyShowFirstFiveProperties: (n: number) =>
    `Preview cards only show the first ${n} properties.`,
  previewMapStyle: 'Preview Map Style',
  previewOptions: 'Show this property in item previews?',
  previewProperties: 'Preview Properties',
  previewPropertiesText:
    'The selected properties are used in the item preview and show up in the permit export',
  previewPropertyLockedOnTemplate:
    'This property is marked as preview on the register template and cannot be removed',
  previous: 'Previous',
  previouslyAdded: 'Previously Added',
  previousOrganisationFeatureFlags: 'Previous Organisation Feature Flags',
  primary: 'Primary',
  priorities: 'Priorities',
  priority: 'Priority',
  prioritySetsTheDueDate: 'Priority sets the Due Date',
  proceed: 'Proceed',
  proceedCreateZonesNow:
    'Proceed to begin, or exit to return to Locations Hierarchy.',
  proceedWithoutAssignAlert: 'You must assign someone before proceeding',
  processing: 'Processing',
  processingPlan: 'Processing Plan',
  processingPriority: 'Processing priority',
  processingStatus: 'Processing Status',
  processingTitle: 'Processing title',
  procore: 'Procore',
  procoreIntegration: 'Procore Integration',
  procoreProjectIdIsRequired: 'Procore Project ID is required',
  procoreProjectIdPlaceholder: 'Enter Procore project ID',
  profile: 'Profile',
  profileDetails: 'Profile Details',
  profileSaved: 'Profile saved',
  project: 'Project',
  projectAdded: 'Project Added',
  projectAdditions: 'Project Additions',
  projectAdmin: 'Project Admin',
  projectAdminInfo:
    'Project Admins are superusers that can manage all project settings',
  projectAdminTooltip:
    'Has full access to both web and mobile app including ability to issue, reject, extend and close permits. Project Admins can invite new users to SiteAssist and edit their roles.',
  projectCode: 'Project Code',
  projectCreated: 'Project Created',
  projectDocuments: 'Project Documents',
  projectIsIn: 'Project Is In',
  projectLayers: 'Project Layers',
  projectLocation: 'Project Location',
  projectName: 'Project Name',
  projectOverview: 'Project Overview',
  projectPermissions: 'Project Permissions',
  projectRemoved: 'Project Removed',
  projects: 'Projects',
  projectSettings: 'Project Settings',
  projectSlashLocation: 'Project/Location',
  projectsSelected: 'Projects Selected',
  projectStatus: 'Project Status',
  projectsWithProjectAdminRoleOrAuthPersonRole:
    'You are not a Project Admin or Permit Approver in any projects',
  projectTemplates: 'Project Templates',
  projectTemplatesHeaderMessage:
    'Click on a Permit Template to make project-specific configurations. Organisation level configurations must be updated by an Account Owner or SiteAssist Support.',
  projectTemplateVersion: 'Project Template Version',
  projectTimeline: 'Project Timeline',
  projectUpdated: 'Project Updated',
  projectUpdates: 'Project Updates',
  projectUsers: 'Project Users',
  projectUserTooltip:
    'Can request a Permit to Work for themselves but also request and submit a permit to work covering multiple Users.',
  projectViewer: 'Project Viewer',
  projectViewerInfo:
    'Project Viewers can view all project data but cannot make any changes or create any permits or checklists',
  projectViewerInfoKioskMode: (isAccountViewer: boolean) =>
    `Enabling Kiosk Mode will turn SiteAssist into a touch friendly tablet like experience for on site project overviews.${
      isAccountViewer ? '' : ' Organisation user must be an Account Viewer.'
    }`,
  projectViewerTooltip:
    'Can see all the permits in the project but cannot create or approve permits.',
  proofOptional: 'Proof optional',
  proofPhoto: 'Proof Photo',
  proofRequired: 'Proof Required',
  proofs: 'Proofs',
  proofUploadFailed: 'One or more proofs failed to upload',
  properties: 'Properties',
  propertiesUpdated: 'Properties updated',
  property: 'Property',
  propertyChange: 'Property Change',
  propertyComponents: 'Property Components',
  propertyDeletedSuccessfully: 'Property deleted successfully',
  propertyName: 'Property Name',
  propertyUpdatedSuccessfully: 'Property updated successfully',
  publish: 'Publish',
  published: 'Published',
  publishedDescription:
    'Making this template live will allow this template to be used in permit requests.',
  publishedPermitTemplate: 'Published Permit Template',
  publishPlan: 'Publish Plan',
  publishPlanDescriptionModal:
    'Once published, the zones on this plan will not be editable.',
  publishPlanDescriptionModal2:
    'Please ensure there are no overlapping zones before publishing.',
  QRCode: 'QR Code',
  qrCodeExpired: 'The QR Code has expired',
  query: 'Query',
  question: 'Question',
  questionAdditionalInformation: (optional: boolean) =>
    `Additional Information ${optional ? '(Optional)' : ''}`,
  questionBuilder: 'Question Builder',
  questionBuilderDescription:
    'Use the Question Builder to create questions that users have to complete at each stage.',
  questionComponents: 'Question Components',
  questionComponentsReadOnlyDescription:
    'This template version is active. Create a new version to make changes.',
  questionDeleted: 'Question deleted',
  questionGroup: 'Question Group',
  questionGroupIndex: (index: number) => `Question Group ${index}`,
  questionIsRequired: 'Question is required',
  questions: 'Questions',
  questionSaved: 'Question saved',
  questionSet: 'Question Set',
  questionSpecificWarnings: 'Question Specific Warnings',
  questionTitle: 'Question Title',
  questionType: 'Question Type',
  quickFilters: 'Quick Filters',
  quizAddAnswer: 'Add Answer',
  quizAnswerLabel: (index: number) => `Answer ${index}`,
  quizAtLeastOneValidAnswer: 'Pick at least one valid answer',
  quizAtLeastTwoAnswers: 'At least two answers are required',
  quizInvalidAnswerText: 'Invalid Answer Text',
  quizQuestion: 'Quiz Question',
  quizQuestionPrompt: 'Question',
  quizSelectAValidAnswer: 'Select valid answers',
  quizValidAnswers: 'Valid Answer(s)',
  raiseAnotherObservation: 'Raise Another Observation',
  raiseChecklist: 'Raise Checklist',
  raiseInspection: 'Raise Inspection',
  raiseObservation: 'Raise Observation',
  raiseObservationDescription: 'Start a new observation',
  raisePermit: 'Raise Permit',
  rams: 'RAMS',
  range: (min = 1, max = 1) => `Range: Max ${max} and Min ${min}`,
  reactivateUser: 'Reactivate User',
  reactivateUserMessage:
    'This will reactivate the user within this project ONLY.',
  read: 'Read',
  readAndUnread: 'Read and unread',
  readMore: 'Read More',
  reason: 'Reason',
  reasonForCancelDots: 'Reason for Cancel...',
  reasonForCancellation: 'Reason for cancellation',
  reasonForClosingObservation: 'Reason for closing',
  reasonForExtension: 'Reason for Extension',
  reasonForExtensionRequest: 'Reason for Extension Request',
  reasonForReducingDuration: 'Reason for reducing duration',
  reasonForRejection: 'Reason for Rejection',
  reasonForRejectionDots: 'Reason For Rejection',
  reasonForResume: 'Reason for Resume',
  reasonForTransfer: 'Reason for Transfer',
  reasonForTransferDots: 'Reason for Transfer...',
  reasonForUpdate: 'Reason for Update',
  reasonForUpdateDescription:
    'You can leave a note to describe the changes you made',
  reasoning: 'Reasoning',
  reassignTask: 'Reassign Task',
  reassignTaskDescription: 'Assign another SiteAssist user',
  reassignTaskNote: 'Reassign Note',
  reBrief: 'Re-brief',
  recalculateFormulas: 'Recalculate Formulas',
  receivePermit: 'Receive Permit',
  receiver: 'Receiver',
  recent: 'Recent',
  recipient: 'Recipient',
  recipients: 'Recipients',
  recordAudio: 'Record Audio',
  recordAVoiceNote: 'Record a voice note',
  recorededVideo: 'Recorded Video',
  rectangle: 'Rectangle',
  recurringCheck: 'RECURRING CHECK',
  redirectedToSSODescription:
    'You will now be directed to your company Single Sign-on.\n\nPlease use your company credentials to log in.',
  redraw: 'Redraw',
  register: 'Register',
  registerCreated: 'Register Created',
  registerDetails: 'Register Details',
  registerItem: 'Register Item',
  registerItemArchived: 'Register Item Archived',
  registerItemAuthGroupDescription:
    'Select the authorisation group for the user select property',
  registerItemCreated: 'Register Item Created',
  registerItemDetails: 'Register Item Details',
  registerItemEmailNotificationSent: 'Register Item Email Notification Sent',
  registerItemPermissionsDescription:
    'Select which authorisation groups are allowed to add or update items',
  registerItems: 'Register Items',
  registerItemUnarchived: 'Register Item Unarchived',
  registerItemUpdated: 'Register Item Updated',
  registerName: 'Register Name',
  registerPermissions: 'Register Permissions',
  registerProperties: 'Register Properties',
  registerPropertiesImportInvalidJson:
    'This is not a valid register properties JSON. Check the content and try again.',
  registerPropertiesImportPastePlaceholder:
    'Paste the exported register properties JSON here',
  registerProperty: 'Register Property',
  registerRoleDeleted: 'Register Role Deleted',
  registerRoles: 'Register Roles',
  registers: 'Registers',
  registerTemplate: 'Register Template',
  registerTemplateProperties: 'Register Template Properties',
  registerTemplates: 'Register Templates',
  registerUpdated: 'Register Updated',
  registerUploadTimeout: 'Register upload timeout',
  reject: 'Reject',
  rejectChecklist: 'Reject Checklist',
  rejectDescription: 'Reject the Permit Request',
  rejected: 'Rejected',
  rejectedBy: 'Rejected By',
  rejectedExtension: 'Extension Rejected',
  rejectExtension: 'Reject Extension',
  rejectionDetails: 'Rejection Details',
  rejectionReason: 'Rejection reason',
  rejectionReasonDescription:
    'Please provide the reason that this request has been rejected.',
  rejectionReasonRequired:
    'You need to provide a reason for rejection before submitting.',
  rejectionReasonTooLong: 'Rejection reason must be at most 2000 characters',
  rejectObservation: 'Reject Observation',
  rejectObservationDescription: 'Mark the Observation as invalid',
  rejectObservationReasonDescription:
    'Please provide a reason for rejecting the observation.',
  rejectPermit: 'Reject Permit',
  rejectPermitExtension: 'Reject Permit Extension',
  rejectResumeDeclaration:
    'If you want to reject the extension please provide a reason.',
  rejectResumePermitDeclaration:
    'By signing this you acknowledge your responsibility as the current Permit Holder and agree to suspend the permit.',
  rejectTransfer: 'Reject Transfer',
  reload: 'Reload',
  remaining: 'Remaining',
  remoteBriefingAccepted: 'Remote Briefing Accepted',
  remoteBriefingDeclined: 'Remote Briefing Declined',
  remove: 'Remove',
  removeBoundary: 'Remove Boundary',
  removedChecklistTemplateFromProject:
    'Checklist Template Removed from Project',
  removedPermitTemplate: 'Permit Template Removed from Project',
  removedUserFromAuthGroup: 'Removed User from Auth Group',
  removedUserFromProject: 'Removed User from Project',
  removeFile: 'Remove File',
  removeFilter: 'Remove Filter',
  removeFromProject: 'Remove from Project',
  removeGroupPermissionsMessage:
    'Removing all permissions will delete the group from the list. Are you sure you want to continue?',
  removeMaxBounds: 'Remove Max Bounds',
  removeParticipant: 'Remove Participant',
  removePermission: 'Remove Permission',
  removeRegisterItemFormulaOverrideWarning:
    'This will remove the override and allow the formula to be calculated according to configuration.',
  removeTransferUser: 'Remove Transfer User',
  removeUser: 'Remove User',
  removeUserFromCompanyConfirmation:
    'This will remove {0} from {1}, including any roles they hold in this company.',
  rename: 'Rename',
  renameFile: 'Rename File',
  renameFolder: 'Rename Folder',
  renameLevel: 'Rename Level',
  renameLocation: 'Rename Location',
  reopen: 'Reopen',
  reopenForApproval: 'Reopen for Approval',
  reopenTask: 'Reopen Task',
  reopenTaskDescription: 'Move task back into workflow',
  repeatsEvery7Days: 'Repeats every 7 days.',
  reportAsIncorrect: 'Report as incorrect',
  reportDownloadComplete: 'Report Download Complete',
  reportDownloadFailed: 'Report Download Failed',
  reportedBy: 'Reported by',
  reportObservation: 'Report Observation',
  reports: 'Reports',
  reportType: 'Report Type',
  request: 'Request',
  requestAPermitTitle: 'Request a Permit',
  requestChanges: 'Request Changes',
  requestChangesDescription:
    'Please provide details as to why the permit request cannot be granted at this time',
  requestDetails: 'Request Details',
  requested: 'Requested',
  requestedBy: 'Requested By',
  requestedChange: 'requested a change to this permit request.',
  requestedExtension: 'Extension Requested',
  requesterConfirmationText:
    'By signing this I certify that I have inspected the area in which the works were carried out, that all tools and materials have been removed and that no risk exists from the works specified in this permit and the permit is now closed.',
  requestExtension: 'Request Extension',
  requestExtensionDescription: 'Request an extension for the permit duration',
  requestExtensions: 'Request Extensions',
  requestMessage: 'Request Message',
  requestPermit: 'Request Permit',
  requestPermitAgain: 'Request Permit Again',
  requestSummary: 'Request Summary',
  requestToResumePermit: 'Request to resume permit',
  requestToResumePermitRejected: 'Request to resume permit rejected',
  requireAnnotations: 'Require annotations',
  requireApproval: 'Require approval',
  requireBriefingAcceptance: 'Do participants need to accept the briefing?',
  requireBriefingAcceptanceHint:
    'When set to Yes, users will be required to accept the briefing in the app, otherwise they will not be logged as a participant.',
  requireBriefingVerification: 'Do briefings require photo verification?',
  required: 'Required',
  requiredByDefault: 'Required by default',
  requiredByDefaultDescription:
    'Automatically assigned to every company on approval. When disabled, the approver selects whether to apply it during company approval.',
  requiredForEveryCompany: 'Required for every company',
  requireGeolocation: 'Require Geolocation?',
  requireGeolocationForBriefings: 'Do briefings require geolocation?',
  requireGeolocationForPermit: 'Do permits require geolocation?',
  requireGeolocationForPostPermitChecks:
    'Do post permit checks require geolocation?',
  requirePhotoFromCamera: 'Require photo to be taken with camera',
  requirePhotoFromCameraNote:
    'Requires photo from the camera, please save a draft and upload the photo on mobile.',
  requirePhotoFromCameraNoteOptional:
    'If attaching a photo, it must come from the camera. To do so, please save a draft and continue the permit on a mobile device',
  requireProofFromCamera: 'Require proof to be taken with camera',
  requireProofFromCameraDescription:
    'Specifies whether a proof has to be a picture taken with the camera or is allowed to be from the gallery.',
  requireProofFromCameraInstructions:
    'If attaching a photo, it must come from the camera. To do so, please save a draft and continue the permit on a mobile device',
  requireProofFromCameraNote:
    'Requires proof from the camera, please save a draft and upload the proof on mobile.',
  requiresAdditionalInformation: 'Requires Additional Information',
  requiresAdditionalInput: 'Requires Input',
  requiresApprovalChecklistTooltip:
    'When enabled, checklists created from this template require approval before completion.',
  requiresDocument: 'Document Required',
  requiresFinalSignOffQuestion:
    'Does the Permit require Final Signoff to be Closed?',
  requiresProof: 'Requires Proof',
  requiresResponse: 'Requires Response',
  requiresSignature: 'Requires signature',
  requiresTransferApproval: 'Does Permit Require Transfer Approval',
  requireSubmissionText: 'Require submission text',
  resendCode: 'Resend Code',
  resendEmail: 'Re-send Email',
  resendInvite: 'Resend invite',
  reset: 'Reset',
  resetGdprConsentConfirmation:
    'Are you sure you want to reset the GDPR consent? This action cannot be undone.',
  resetPassword: 'Reset Password',
  resetPasswordCheckInbox: 'Check your inbox to finish resetting your password',
  resetToProjectBoundaries: 'Reset camera to project boundaries',
  resizeItemsInstructions:
    'Move and/or resize the items to their intended location, then press Done.',
  resPerson: 'Res. Person',
  respond: 'Respond',
  respondToChanges: 'Respond to Changes',
  respondToChangesDescription:
    'These are the changes that have been requested.',
  response: 'Response',
  responseIsRequired: 'Response is required',
  restart: 'Restart',
  restore: 'Restore',
  restrictToInspectionTemplates: 'Restrict to Inspection Templates',
  restrictToProjects: 'Restrict to Projects',
  resume: 'Resume',
  resumeAfterSuspension: 'Resume After Suspension',
  resumeAfterSuspensionTitle: 'Who can resume after suspension?',
  resumed: 'Resumed',
  resumePermit: 'Resume Permit',
  resumePermitDeclaration:
    'By signing this you acknowledge that the permit will be resumed, and activities can recommence in the work area.',
  resumePermitDescription: 'Resume this Permit',
  resumePermitReason: 'Reason for resuming',
  resumePermitRequest: 'Resume Permit Request',
  resumePermitRequested: 'Resume Permit Requested',
  resumeRequestApproved: 'Resume Request Approved',
  resumeRequested: 'Resume Requested',
  resumeRequestReason: 'Resume Request Reason',
  resumeRequestRejected: 'Resume Request Rejected',
  retry: 'Try Again',
  retrying: 'Loading',
  retryOnError: 'Error - tap to retry',
  returnToMainLinkedProcess: 'Return to Main Document',
  returnType: 'Return Type',
  reUploadDocument: 'Re-upload Document',
  review: 'Review',
  reviewAnswer: 'Review Answer',
  reviewCompany: 'Review Company',
  reviewDocument: 'Review Document',
  reviewDocuments: 'Review Documents',
  reviewedBy: 'Reviewed by',
  reviewedByOnX: (name: string, date: string) =>
    `Reviewed by ${name} • ${date}`,
  reviewExtension: 'Review Extension',
  reviewIssuedAnswers: 'Review Issued Answers',
  reviewPermitInfo: 'Please review the information in the Permit.',
  reviewPermitIssue:
    'Please review the information provided by the issuer of the permit.',
  reviewPermitTransfer: 'Review Permit Transfer',
  reviewQuestionForChecklist: 'Review the questions for the checklist',
  reviewQuestionForPermit: 'Review the questions for the permit',
  reviewResumeRequest: 'Review Resume Request',
  reviewResumeRequestDeclaration:
    'By approving this request, you acknowledge that the permit will be resumed, and all work activities may recommence. By rejecting this request, you acknowledge that the permit will remain suspended, and work activities must not recommence.',
  reviewSubmission: 'Review Submission',
  reviewTranscript: 'Review Transcript',
  revoke: 'Revoke',
  revoking: 'Revoking...',
  riskAndTriggers: 'Risk & Triggers',
  role: 'Role',
  roleAccessDescriptions: {
    ADD_PARTICIPANT: 'Add permit participants',
    PERIODIC_CHECK_SUBMIT: 'Submit periodic checks',
    VIEW_ONLY: 'View permit access',
  },
  roleAccountOwnerTooltip:
    'Have access to all Organisation and Project settings. Can create templates to be used by projects as well as editing and viewing all projects.',
  roleAccountViewerTooltip:
    'Can see all data in an organisation. Actions they can perform are controlled by project membership',
  roleCreated: 'Role Created',
  roleDescription: 'Role Description',
  roleName: 'Role Name',
  rolePermissions: 'Role Permissions',
  roleRequired: 'Role is required',
  roles: 'Roles',
  roleTiedToPrimaryCompany:
    "This role is tied to the user's primary company. To manage other company roles, navigate to the Companies tab in the user's profile.",
  roleUpdated: 'Role Updated',
  roleUserTooltip:
    'A User is invited to your account and can have multiple roles depending on permission level on a project level. Can be Project Admin, Permit Approver, User, or Project Viewer in projects.',
  rootRole: 'Root Role',
  rotateScreen: 'Rotate Screen',
  row: 'Row',
  ruleIndex: (index: number) => `Rule ${index}`,
  rulesFromOtherStages: 'Rules from other stages controlling visibility',
  rulesInOtherStages: 'Rules in other stages controlled by this question',
  rulesSetByQuestion: 'Rules set by this question',
  rulesSetByQuestionDescription:
    "Rules governed by this question's answers. Can only add/edit rules for the current question set.",
  runInspection: 'Run Inspection',
  runsCustomIntervalWeekends: (days: number, excludeWeekends: boolean) =>
    `Runs every ${days} days${
      excludeWeekends ? ' excluding' : ' including'
    } weekends`,
  runsEvery30DaysWeekends: (excludeWeekends: boolean) =>
    `Runs every 30 days ${
      excludeWeekends ? 'excluding' : 'including'
    } weekends`,
  runsEverydayWeekends: (excludeWeekends: boolean) =>
    `Runs everyday ${excludeWeekends ? 'excluding' : 'including'} weekends`,
  runsOnceOnly: 'Runs once only',
  satisfactory: 'Satisfactory',
  save: 'Save',
  saveAndExit: 'Save & Exit',
  saveAnnotations: 'Save Annotations',
  saveApprovalDraftDescription:
    'Save your current progress and continue the approval later.',
  saveAreas: (featureCount: number) => `Save ${featureCount} Area(s)`,
  saveCheck: 'Save Check',
  saveDraft: 'Save Draft',
  saveDraftDescription:
    'Write a note so that you can find the draft easily later',
  saveForLater: 'Save for Later',
  saveLocations: 'Save Locations',
  saveNewDraft: 'Save New Draft',
  saveNow: 'Yes, save now',
  saveProjectPlanAreasDescription:
    'Would you like to save your zones before you close/exit?',
  saveRole: 'Save Role',
  saveSelectedProjectPlanAreaDescription:
    'Save to continue raising this permit, or cancel to edit this selection.',
  saveSignature: 'Save Signature',
  saveTemplate: 'Save Template',
  saveToMap: 'Save to Map',
  saveYourFileAsCsvAndUpload: 'Save your file as a .CSV and upload',
  savingDraft: 'Saving draft',
  scanQRCode: 'Scan QR Code',
  scanQRCodeDescription: 'Scan a SiteAssist QR code for quick actions',
  scanQRCodeToGetBriefedDescription:
    'Please use your phone’s camera to scan the QR code to get briefed.',
  scanToJoin: 'Scan to Join',
  scanToJoinDescription:
    'Please use your phone’s camera to scan the QR code to get briefed.',
  scanToOpenOnMobile: 'Scan to open on mobile',
  schedule: 'Schedule',
  scheduleAndDueDate: 'Schedule & Due Date',
  scheduleCreated: 'Schedule created',
  scheduled: 'Scheduled',
  scheduledAutomatically: 'Scheduled Automatically',
  scheduledManually: 'Scheduled Manually',
  scheduleInspectionCompletedRuns: 'Completed Runs',
  scheduleInspectionConfirmStopSchedule:
    'Are you sure you want to stop this inspection schedule?',
  scheduleInspectionCreateSchedule: 'Create Inspection Schedule',
  scheduleInspectionEditInspection: 'Edit Inspection',
  scheduleInspectionEveryNDays: (days: number) => `Every ${days} days`,
  scheduleInspectionLastRun: 'Last Run',
  scheduleInspectionNextDue: 'Next Due',
  scheduleInspectionResumeSchedule: 'Resume',
  scheduleInspectionRunInspection: 'Run Inspection',
  scheduleInspections: 'Schedule Inspections',
  scheduleResumed: 'Schedule resumed',
  scheduleStopped: 'Schedule stopped',
  scheduleUpdated: 'Schedule updated',
  seachByNameOrEmail: 'Search by name or email',
  search: 'Search',
  searchByNameOrEmail: 'Search by Name or Email',
  searchByNameOrId: 'Search by Name or ID',
  searchByRegisterName: 'Search by register name',
  searchInspections: 'Search inspections',
  searchPermits: 'Search Permits',
  searchTemplates: 'Search templates...',
  secondsShort: 's',
  seeAllChecks: 'See All Checks',
  seeChangesOnMap: 'See Changes On Map',
  seeDetails: 'See Details',
  seeMore: 'See more',
  select: 'Select',
  selectAction: 'Select Action',
  selectAll: 'Select All',
  selectAnExistingDocumentOrUpload:
    'Select an existing document or upload a new one',
  SelectAssignee: 'Select assignee',
  selectAuthGroup: 'Select Authorisation Group',
  selectBelowToAssignStatus:
    'Select below to assign a status (pass or fail) to the document',
  selectBelowToPassOrFail: 'Select below to pass or fail the document',
  selectBetween: 'Please select between {min} and {max} options',
  selectBetweenXAndYAnswers: (min: number, max: number) =>
    `Select between ${min} and ${max} answers`,
  selectBriefingParticipants: 'Select Participants to Brief',
  selectChecklistsToLink: 'Select Checklists to Link',
  selectChecklistToView: 'Select a checklist to preview it',
  selectChecklistType: 'Select Checklist Type',
  selectChildPermits: 'Select child permit(s)',
  selectCsvImport: 'Select a CSV file to import or drag and drop it here',
  selectDate: 'Select date',
  selectDateAndTime: 'Select Date & Time',
  selectDates: 'Select Dates',
  selectDefaultTaskTemplate: 'Select Default Task Template',
  selectDestinationOrganisation: 'Select destination organisation',
  selectDiagram: 'Select Diagram',
  selectDocument: 'Select Document',
  selected: (selectedNumber: number) => `${selectedNumber} Selected`,
  selectedCompany: 'Selected Company',
  selectedFrom: (selectedNumber: number, totalNumber: number) =>
    selectedNumber === 0
      ? `${selectedNumber} selected`
      : `${selectedNumber} of ${totalNumber} selected`,
  selectedParticipants: 'Selected Participants',
  selectedZones: 'Selected Zones',
  selectEndDate: 'Select End Date',
  selectFeatureToEditProperties: 'Select a feature to edit its properties',
  selectFilter: 'Select Filter',
  selectFilterCondition: 'Select Filter Condition',
  selectFilterOperator: 'Select filter operator',
  selectInspection: 'Select Inspection',
  selectInspectionsToLink: 'Select Inspections to Link',
  selectInspectionType: 'Select Inspection Type',
  selectItemToView: 'Select an item from the list to preview it',
  selectLanguage: 'Select Language',
  selectLocation: 'Select Location',
  selectLocationOnPlan: 'Select Location on Plan',
  selectNumber: 'Select number',
  selectObservationToView: 'Select an observation to preview it',
  selectOptions: (minOptions: number, maxOptions?: number) =>
    `Please select ${minOptions}${
      maxOptions > minOptions ? ` to ${maxOptions}` : ''
    } option(s)`,
  selectOrTypeInTemplate: 'Select or Type in Template',
  selectOutcomeOfAppraisal: 'Select outcome of Appraisal',
  selectParentPermit: 'Select Parent Permit',
  selectParticipants: 'Select Participants',
  selectPdfToUpload: 'Select a PDF to upload',
  selectPermit: 'Select Permit',
  selectPermitDateRange: 'Select Permit Date Range',
  selectPermitStatus: 'Select Permit Status',
  selectPermitsToLink: 'Select Permits to Link',
  selectPermitToView: 'Select a permit to preview it',
  selectPermitType: 'Select Permit Type',
  selectPermitTypes: 'Select Permit Types',
  selectProject: 'Select Project',
  selectProjects: 'Select Projects',
  selectQuestionPlaceholderText: 'Select Question / Group',
  selectQuestionSetFirst: 'Select a question set first',
  selectQuestionToConfigureAnswers:
    'Select a question to configure answer conditions',
  selectQuestionType: 'Select Question Type',
  selectRegister: 'Select Register',
  selectRegisterItem: 'Select Register Item',
  selectRequirements: 'Select the requirements to assign to this company.',
  selectRole: 'Select Role',
  selectRoleToAddParticipants: 'Select a role to add participants',
  selectStartDate: 'Select Start Date',
  selectStatus: 'Select Status',
  selectSubZone: 'Select Sub Zone',
  selectTaskToView: 'Select a task to preview it',
  selectTemplate: 'Select Template',
  selectTime: 'Select Time',
  selectTransferUser: 'Select Transfer User',
  selectType: 'Select Type',
  selectUser: 'Select User',
  selectView: 'Select View',
  selectXAnswers: (count: number) =>
    `Select ${count} ${count === 1 ? 'answer' : 'answers'}`,
  selectZone: 'Select Zone',
  selectZones: 'Select Zones',
  selfOnboarding: 'Self Onboarding',
  selfRegistration: 'Self Registration',
  selfRegistrationDescription: 'Create your account to get started.',
  selfRegistrationNotAvailable:
    'Self registration is not available for this organisation.',
  send: 'Send',
  sendEmail: 'Send Email',
  sendForgotPasswordLink: 'Send Forgot Password Link',
  sending: 'Sending',
  sendingRequest: 'Sending Request',
  sendRequest: 'Send Request',
  sentToPermitHolder: 'Sent to Permit Holder',
  setAnswers: (answers: string[]) =>
    `Set ${answers?.length || ''} answer${answers?.length > 1 ? 's' : ''}`,
  setBoundary: 'Set Boundary',
  setConditions: 'Set Conditions',
  setExpiry: 'Set Expiry',
  setExpiryDate: 'Set Expiry Date',
  setLocation: 'Set Location',
  setManually: 'Set Manually',
  setTimezone: 'Set Timezone',
  setting: 'Setting',
  settings: 'Settings',
  settingsSaved: 'Settings saved successfully',
  setupAnInspection: 'Setup an Inspection',
  share: 'Share',
  shareLinkDescription: 'Share a link with users and non-users',
  sharePermit: 'Share Permit',
  sharePermitDescription: 'Share a link to this permit to users and non-users',
  shortened: 'Shortened',
  shortenInformation: 'Shorten Information',
  shortenPermit: 'Shorten Permit',
  shortenPermitDescription: 'Reduce the duration of this permit',
  showActiveZonesAndPoints: 'Show Active Zones and Points',
  showAll: 'Show All',
  showAllComments: 'Show All Comments',
  showAllLayers: 'Show All Layers',
  showArchived: 'Show Archived',
  showChecklists: 'Show Checklists',
  showGroupIfAnswer: 'Show Group if answer is:',
  showHideLayer: 'Show/Hide layer',
  showIfAnswerIs: 'Show if answer is',
  showInfo: 'Show Info',
  showLocation: 'Show Location',
  showMenu: 'Show Menu',
  showMore: 'Show More',
  showOnMap: 'Show on map',
  showOriginal: 'Show Original',
  showPermits: 'Show Permits',
  showPhotos: 'Show Photos',
  showPins: 'Show Pins',
  showQRCode: 'Show QR Code',
  showSignature: 'Show Signature',
  showTranslation: 'Show Translation',
  showZones: 'Show Zones',
  sign: 'Sign',
  signAndReject: 'Sign and Reject',
  signAndSubmit: 'Sign and Submit',
  signature: 'Signature',
  signatureOptional: 'Signature Optional',
  signatureRequired: 'Signature Required',
  signed: 'Signed',
  signedBy: 'Signed by',
  signedOffBy: 'Signed Off By',
  signedOn: 'Signed on',
  signedVia: 'Signed via',
  signing: 'Signing',
  signOffPermit: 'Sign Off Permit',
  signSpotInstruction: 'Sign within the dotted lines',
  signUp: 'Sign Up',
  signUpForm: 'Sign Up Form',
  signUpFormUrl: 'Sign Up Form URL',
  singleSignOnUserAlert:
    'This user is configured for Single-Sign using your corporate system. Please speak to your IT support team about resetting you password',
  siteAssist: 'SiteAssist',
  siteBox: 'SiteBox',
  siteBoxConfigurationImported: 'Configuration imported successfully',
  siteBoxConfigurationInvalidFormat: 'Invalid configuration format',
  siteBoxConfigurationUpdated: 'Site Box Configuration updated',
  siteboxDocument: 'SiteBox Document',
  siteBoxDocumentCreated: 'SiteBox Document Created',
  siteBoxDocumentMoved: 'SiteBox Document Moved',
  siteBoxDocumentRenamed: 'SiteBox Document Renamed',
  siteBoxDocumentStatusChanged: 'SiteBox Document Status Changed',
  siteBoxDocumentUpdated: 'SiteBox Document Updated',
  siteBoxDocumentVersion: 'SiteBox Document Version',
  siteBoxDocumentVersionCreated: 'SiteBox Document Version Created',
  siteBoxDocumentVersionStatusChanged:
    'SiteBox Document Version Status Changed',
  siteLocation: 'Site Location',
  siteLocations: 'Site Locations',
  skip: 'Skip',
  skipPostPermitChecksQuestion:
    'Does the Permit Holder need to do Post Permit Checks?',
  skipSsoUsers: 'Skip SSO Users',
  skipTutorial: 'Skip Tutorial',
  sortAscending: 'Sort Ascending',
  sortBy: 'Sort By',
  sortDescending: 'Sort Descending',
  source: 'Source',
  sourceInspection: 'Source Inspection',
  sourceOrganisation: 'Source Organisation',
  ssoConfiguration: 'SSO Configuration',
  ssoConfigurationDeleted: 'SSO configuration deleted',
  ssoConfigurationMissing: 'No SSO configuration found',
  ssoConfigurationUpdated: 'SSO Configuration Updated',
  ssoDeletionConfirmation: (organisationId: number) =>
    `Are you sure you want to identity provider for organisation: ${organisationId} ?`,
  ssoFullRollout: 'SSO full rollout',
  ssoFullRolloutDescription: 'Enables SSO for the entire organisation',
  ssoLoginNotAvailable:
    'SSO login is not available. Please log in with another user',
  ssoRedirectMessage: `You will now be directed to your company's login page. Please use your company credentials to log in.`,
  stage: 'Stage',
  standardValidityXDays: (days: number) => `Standard validity: ${days} days`,
  start: 'Start',
  startDate: 'Start Date',
  startDateAndTime: 'Start date & time',
  startDateMustBeInTheFuture: 'Start date must be in the future',
  startDocumentAppraisal: 'Start Document Appraisal',
  started: 'Started',
  startHere: 'Start Here',
  startReview: 'Start Review',
  starts: 'Starts',
  startsOnAscending: 'Starts (oldest first)',
  startsOnDescending: 'Starts (newest first)',
  startTime: 'Start Time',
  status: 'Status',
  statuses: 'Statuses',
  statusesSelected: 'Statuses Selected',
  statusTrail: 'Status Trail',
  stepDescription: 'Step Description',
  stepName: 'Step Name',
  stepRole: 'Step Role',
  steps: 'Steps',
  stepTitle: 'Step Title',
  stepType: 'Step Type',
  stop: 'Stop',
  stoppingAutomaticallyAt: (time: string) =>
    `Stopping automatically at ${time}`,
  stopSchedule: 'Stop Schedule',
  strictCompanyManagement: 'Strict Company Management',
  strictCompanyManagementDescription:
    'When enabled, users must select from predefined companies only.',
  submissionFailed: 'Submission Failed',
  submissionFailedCheckInternetConnection:
    'Check your internet connection and try submitting the form again.',
  submissionFailedSaveAndSyncLater:
    'If the connection remains unstable, save the submission and sync it later.',
  submissionSavedToMyWork:
    "Your work is saved in My Work — submit it again from there when you're ready.",
  submit: 'Submit',
  submitChecklist: 'Submit Checklist',
  submitObservation: 'Submit Observation',
  submitPeriodicCheck: 'Submit Periodic Check',
  submitPeriodicChecks: 'Submit Periodic Checks',
  submitPermitChanges: 'Submit Permit Changes',
  submitPostPermitChecks: 'Submit Post Permit Checks',
  submitRelevantChecksForPermit: 'Submit relevant checks for this permit',
  submitSignature: 'Submit Signature',
  submitTask: 'Submit Task',
  submitted: 'Submitted',
  submittedBy: 'Submitted By',
  submitter: 'Submitter',
  succesfullyMovedFile: 'Successfully moved file',
  succesfullyMovedTemplate: 'Successfully moved template',
  successfullyDeleted: 'Successfully Deleted',
  successfullyImportedRows: (rowNumber: number) =>
    `Successfully imported ${rowNumber} row${rowNumber > 1 ? 's' : ''}`,
  successfullyUploadedItems: 'Successfully uploaded Items',
  successfullyUploadedItemsToSiteBox: 'Successfully uploaded items to Site Box',
  summary: 'Summary',
  superAdmin: 'Super Admin',
  supervisor: 'Supervisor',
  supervisorTooltip:
    'Can request an individual permit to work for themselves but also request and submit a permit to work covering multiple Operatives. Can view all permits related to them.',
  support: 'Support',
  supportInfo: "Message us and we'll be right with you",
  supportUser: 'Support User',
  supportUserProfile: 'Support User Profile',
  supportView: 'Support View',
  suspended: 'Suspended',
  suspendedBy: 'Suspended By',
  suspendPermit: 'Suspend Permit',
  suspendPermitDeclaration:
    'By signing this you acknowledge that this permit will be suspended, and all activities in the work area must be stopped until the permit is resumed.',
  suspendPermitDescription: 'Temporarily suspend this permit',
  suspendPermitReason: 'Reason for suspension',
  suspensionReason: 'Suspension Reason',
  switchOrganisation: 'Switch Organisation',
  switchToDiagramView: 'Switch to Diagram View',
  switchUser: 'Switch User',
  syncAll: 'Sync All',
  syncAutoRetryStopped:
    'Automatic retries stopped — sync manually to try again.',
  synced: 'Synced',
  syncedAt: 'Synced at',
  syncedFromSyncTray: 'Synced from sync Tray',
  syncFailedDefault: "Couldn't sync — please try again.",
  syncLater: 'Sync Later',
  syncNow: 'Sync Now',
  table: 'Table',
  tableOfContents: 'Table of Contents',
  tagArchived: 'Tag archived',
  tagCreated: 'Tag created',
  tags: 'Tags',
  tagUpdated: 'Tag updated',
  takePhoto: 'Take Photo',
  takePhotoAction: 'take a photo',
  tapAgainToViewPermitDetails: 'Tap again to view permit details',
  tapOnAFileForSelection: 'Tap on a file for selection',
  tapToCaptureSignature: 'Tap to capture signature',
  tapToEditSignature: 'Tap to edit signature',
  task: 'Task',
  taskActions: 'Task Actions',
  taskAssignedNotifications: 'Task assigned to you',
  taskCancelledNotifications: 'Task cancelled',
  taskCancelledSuccessfully: 'Task cancelled successfully',
  taskCompletedNotifications: 'Task completed',
  taskCreatedSuccessfully: 'Task created successfully',
  taskDeletedSuccessfully: 'Task deleted successfully',
  taskDownloadComplete: 'Task Download Complete',
  taskDownloadFailed: 'Task Download Failed',
  taskDraftSaved: 'Task Draft Saved',
  taskDueDateReminderSentNotifications: 'Task due in 24 hours',
  taskEditedSuccessfully: 'Task edited successfully',
  taskLockedUntil: (datetime: string, durationLabel: string) =>
    `Task locked until ${datetime} (${durationLabel} before permit start time)`,
  taskMovedSuccessfully: 'Task moved successfully',
  taskMovedToCompletedSuccessfully: 'Task moved to completed successfully',
  taskMustBeCompletedOnMobile: 'Task must be completed on mobile.',
  taskName: 'Task Name',
  taskPermissions: 'Task Permissions',
  taskReopenedSuccessfully: 'Task reopened successfully',
  taskRequestedChangesSuccessfully: 'Task requested changes successfully',
  tasks: 'Tasks',
  taskScheduling: 'Task Scheduling',
  taskSchedulingDescription:
    'Schedule task completion ahead of permit start time.',
  tasksRaised: 'Tasks Raised',
  tasksResolvedOnTime: 'Tasks Resolved on Time',
  taskSummary: 'Task Summary',
  taskTemplate: 'Task Template',
  taskTemplates: 'Task Templates',
  taskType: 'Task Type',
  taskUpdatedNotifications: 'Task updated',
  taskUpdatedSuccessfully: 'Task updated successfully',
  team: 'Team',
  teamsAndRoles: 'Team and Roles',
  telephone: 'Telephone',
  telephoneNumber: 'Telephone number',
  template: 'Template',
  templateAddedToProject: 'Template added to Project',
  templateArchived: 'Template Archived',
  templateBuilder: 'Template Builder',
  templateCode: 'Template Code',
  templateCodeIsRequired: 'Template code is required',
  templateCreated: 'Template created',
  templateDeleted: 'Template deleted',
  templateDescription: 'Template Description',
  templateName: 'Template Name',
  templateNotInProject: 'Template not active in project',
  templateOnlyViewWarningDescription:
    'If you think there should be changes made to this template please speak to:',
  templateOnlyViewWarningDescriptionOwner:
    'Please go to the organisation level to edit it: ',
  templateOnlyViewWarningTitle:
    'Templates can only be edited at the Organisation level by an Account Owner',
  templateOnlyViewWarningTitleOwner:
    'Templates can only be edited at the Organisation level',
  templatePermitRoles: 'Permit Roles',
  templatePublished: 'Template Published',
  templateQuestionsText: (
    noPermitHolderQuestions: boolean,
    noAuthPersonQuestions: boolean,
  ) => {
    return localizedStrings.formatString(
      localizedStrings.emptyQuestionsMessage,
      `${noPermitHolderQuestions ? localizedStrings.permitHolder : ''} ${
        noPermitHolderQuestions && noPermitHolderQuestions ? ' and ' : ''
      } ${noAuthPersonQuestions ? localizedStrings.authorisedPerson : ''}`,
      `${noPermitHolderQuestions && noAuthPersonQuestions ? 's' : ''}`,
    ) as string
  },
  templateRemovedFromProject: 'Template removed from Project',
  templates: 'Templates',
  templateSettings: 'Template Settings',
  templatesUpdated: 'Templates Updated',
  templateTitle: 'Template Title',
  templateTitleIsRequired: 'Template title is required',
  templateTypeIsRequired: 'Template type is required',
  templateUnarchived: 'Template Unarchived',
  templateUniqueCode: 'Template Unique Code',
  templateUnpublished: 'Template Unpublished',
  templateUpdated: 'Template updated',
  templateVersion: 'Template Version',
  templateVersionUpdated: 'Template Version Updated',
  test: 'Test',
  testOrganisation: 'Test Organisation',
  testOrganisations: 'Test Organisations',
  text: 'Text',
  textSection: 'Text Section',
  theFollowingPropertiesWillBeCalculated:
    'The following properties will be calculated',
  thenShow: 'Then show',
  thePermitWasCancelled:
    'The Permit Request was cancelled. Please see below for more information.',
  thePermitWasRejected:
    'The Permit Request was rejected. Please see below for more information.',
  theSelectedExpiryTimeCannonExceedXDaysYHours: (expiryDateTime) =>
    `The selected expiry time cannot exceed ${expiryDateTime}`,
  thisFieldIsRequired: 'This field is required',
  thisGroupHasNoMembers: 'This group has no members',
  thisGroupHasNoPermits: 'This group has no permits',
  thisInspectionHasAMaximumExpiryOfXDaysYHours: (expiryDateTime) =>
    `This inspection has a maximum expiry of ${expiryDateTime}`,
  thisMonth: 'This month',
  thisOrganisationHasNoApiKeys: 'This organisation has no API keys',
  thisOrganisationHasNoAuthentication:
    'This organisation has no authentication configuration',
  thisPermitWillBeAppliedToSelectedZone:
    'This permit will be applied to the zone you have selected',
  thisProjectHasNoGroups: 'This project has no groups',
  thisPropertyIsCalculatedAutomatically:
    'This property is calculated automatically',
  thisQuestionsVisibility: "This question's visibility",
  thisTemplateHasNoProjects: 'This template has not been added to any projects',
  thisWeek: 'This week',
  thisWillDeleteTheFollowingRole: 'This will delete the following role',
  thisWillRemoveParticipant: 'This will remove the participant.',
  time: 'Time',
  timeLeft: (duration: string) => `${duration} Left`,
  timeline: 'Timeline',
  timeOfDay: 'Time of Day',
  timeOverdue: (time: string) => `${time} (Overdue)`,
  timeTravelerMessage:
    "It looks like your device's clock is set to the past. Unfortunately, our app hasn't invented time travel compatibility yet.\n\nPlease reset your date to the present to continue.",
  timeUntilPermitExpires: 'Time until permit expires',
  timezone: 'Timezone',
  title: 'Title',
  to: 'to',
  toAddOne: 'to add one.',
  toastTesting: 'Toast testing',
  toConfirm: 'to confirm',
  today: 'Today',
  toDo: 'To-do',
  toEditTheAnnotationsOfThisItemSwitchToDiagramView:
    'To edit the annotations of this item, switch to the Diagram View.',
  toggleProjectLocationInfo:
    'Turning on project locations will enable the below Location/Area heirarchy in the mobile app.  Turning this off will allow for free text locations in mobile app actions and the Location/Area heirarchy below will be ignored.',
  tomorrow: 'Tomorrow',
  tooFewSelected: 'Too few selected',
  tooltipExpired:
    'Expired permits. Permit Holder must declare that work is complete or be Revoked by an authorised user.',
  tooltipExtensionRequested: 'Permits that have an extension request',
  tooltipFinalSignoff:
    'Works and potential post permit checks are completed. An authorised user must and review and sign off to close the permit.',
  tooltipIssued: 'Permits that are currently in a Pre-Start state',
  tooltipOpen: 'Permits that are currently Open',
  tooltipPostPermitChecks:
    'Work has been declared complete and the Permit Holder must complete checks or wait for a countdown (fire watch for example)',
  tooltipRequested:
    'Permits requested on Site. Must be reviewed by an authorised user.',
  tooManySelected: 'Too many selected',
  topicArea: 'Topic Area',
  total: 'Total',
  totalChecklists: 'Total Checklists (all time)',
  totalDrawnAreaSize: 'Total drawn area size:',
  totalPermits: 'Total Permits (all time)',
  transcriberNotReady: 'Transcriber is not ready',
  transcribing: 'Transcribing',
  transcript: 'Transcript',
  transcriptionFailed: 'Transcription failed',
  transcriptionLanguage: 'Transcription Language',
  transfer: 'Transfer',
  transferAccepted: 'Transfer Accepted',
  transferApproved: 'Transfer Approved',
  transferAwaitingApproval: 'Transfer Awaiting Approval',
  transferAwaitingRecipient: 'Transfer Awaiting Recipient',
  transferCanceled: 'Transfer Canceled',
  transferDeclined: 'Transfer Declined',
  transferDetails: 'Transfer Details',
  transferFrom: 'Transfer From',
  transferInformation: 'Transfer Information',
  transferingTo: 'Transfering To',
  transferInitiated: 'Transfer Initiated',
  transferInitiatedBy: 'Transfer Initiated By',
  transferItemsToDiagram: 'Transfer Items to Diagram',
  transferOwnershipOnBriefing: 'Transfer Permit Ownership on Briefing',
  transferPermit: 'Transfer Permit',
  transferPermitDescription:
    'Transfer the ownership of this permit to another SiteAssist user',
  transferPermitDisclaimer:
    'By signing this transfer you acknowledge that the permit will change ownership. You will no longer be the permit holder.',
  transferReason: 'Transfer Reason',
  transferRejected: 'Transfer Rejected',
  transferRequest: 'Transfer Request',
  transferRequested: 'Transfer Requested',
  transferRequestMade:
    'A request has been made to transfer the ownership of this permit to',
  transferSent: 'Transfer Sent',
  transferTo: 'Transfer To',
  trendsAndAlerts: 'Trends & Alerts',
  trigger: 'Trigger',
  triggers: 'Triggers',
  type: 'Type',
  typeDeleteToConfirm:
    'Type <span class="text-red font-weight-bold">DELETE</span> to confirm',
  typeHere: 'Type here...',
  typeOrSelect: 'Type or Select',
  typeOrSelectCompany: 'Type or Select Company',
  typeOrSelectUser: 'Type or Select User',
  typeXToConfirm: (text: string) =>
    `Type <span class="text-red font-weight-bold">${text}</span> to confirm`,
  unansweredInspectionItems: 'Unanswered Inspection Items',
  unansweredInspectionItemsCountMessage: (count: number) =>
    `You have ${count} inspection item${
      count > 1 ? 's' : ''
    } that are currently unanswered. If you proceed, these will be marked as N/A.`,
  unansweredInspectionItemsMessage:
    'Unanswered inspection items will be marked as N/A',
  unarchive: 'Unarchive',
  unarchivedChecklistTemplate: 'Checklist Template Unarchived',
  unarchivedPermitTemplate: 'Unarchived Permit Template',
  unassign: 'Unassign',
  unassignCompliance: 'Unassign Compliance',
  unassignComplianceConfirmation: 'This will remove {0} from {1}.',
  unauthorisedAction: 'You are not authorised to perform this action.',
  unauthorisedActions: 'You are not authorised to perform these actions.',
  unauthorized: 'UNAUTHORIZED',
  uncaughtError:
    'Apologies, an unexpected error occurred. The issue has been automatically reported to our team.',
  unclosedAreas: 'Unclosed Areas',
  unclosedAreasMessage:
    'You have an unclosed area, do you want to close it and save?',
  undo: 'Undo',
  unexpectedErrorMessage:
    'Seems we had an unexpected error. The SiteAssist team has been notified and will look into this as a priority!',
  unfinished: 'Unfinished',
  unimpersonateUser: 'Unimpersonate User',
  unimpersonateUserFailed: 'Failed to unimpersonate user',
  unimpersonateUserSuccess: 'Unimpersonated user successfully',
  uniqueCode: 'Unique Code',
  uniqueId: 'Unique ID',
  uniqueIdSystemControlled: 'Unique ID System Controlled',
  unknown: 'Unknown',
  unknownAddress: 'Unknown Address',
  unpublish: 'Unpublish',
  unpublished: 'Unpublished',
  unpublishedPlan: 'Unpublished Plan',
  unread: 'Unread',
  unreadOnly: 'Unread only',
  unresolved: 'Unresolved',
  unsavedAnnotations: 'Unsaved Annotations',
  unsavedAnnotationsMessage:
    'You have unsaved annotations on this PDF. If you go back now, your changes will be lost.',
  unsavedChanges: 'Unsaved Changes',
  unset: 'Unset',
  unsigned: 'Unsigned',
  unsignedParticipant: (n: number) =>
    `${n} unsigned participant${n === 1 ? '' : 's'}`,
  unsupportedFileType: 'Unsupported file type',
  unsyncedActions: 'Unsynced Actions',
  untitledQuestion: 'Untitled question',
  update: 'Update',
  updateAvailable: 'Update Available',
  updateAvailableDescription: 'An update is available that must be installed.',
  updateBriefingSession: 'Update Briefing Session',
  updateChecklistDraft: 'Update Checklist Draft',
  updateColumnValue: 'Update Column Value',
  updated: 'Updated',
  updatedAuthGroupPermissions: 'Updated Auth Group Permissions',
  updatedBy: 'Updated by',
  updatedChecklistTemplate: 'Checklist Template Updated',
  updatedChecklistVersion: 'Checklist Version Updated',
  updatedFrom: 'Updated from',
  updatedOrganisationPermissions: 'Updated User Permissions – Organisation',
  updatedOrgAuthGroupPermissions: 'Updated Auth Group Permissions',
  updatedPermitApprovalSettings: 'Updated Permit Approval Settings',
  updatedPermitTemplate: 'Permit Template Updated',
  updatedPermitTemplateVersion: 'Permit Version Updated',
  updatedProjectChecklistVersionSettings:
    'Project Checklist Version Settings Updated',
  updatedProjectPermissions: 'Updated User Permissions – Project',
  updatedProjectPermitSettings: 'Updated Permit Settings – Project',
  updatedProperties: 'Updated Properties',
  updateDraft: 'Update Draft',
  updateForm: 'Update Form',
  updateItem: 'Update Item',
  updateItems: 'Update Items',
  updateNow: 'Update Now',
  updatePermitDraft: 'Update Permit Draft',
  updateRegisterItem: 'Update Register Item',
  updateRegisterItems: 'Update Register Items',
  updateRowValues: 'Update Item',
  updateTaskDraft: 'Update Task Draft',
  updateUser: 'Update User',
  upload: 'Upload',
  uploadADiagramToStartCreatingNewItems:
    'Upload a diagram to start creating new items.',
  uploadAPhoto: 'Upload a Photo',
  uploadCompaniesImportant1: 'Use the provided template to enter company data.',
  uploadCompaniesStep2: 'Populate template with company data',
  uploadCompaniesStepsDescription: 'Follow these steps to upload in bulk.',
  uploadComplete: 'Upload Complete',
  uploadCsvImportant1: 'Use the provided template to enter data.',
  uploadCsvImportant2: "Don't add new columns or edit column headers.",
  uploadCsvStep1: 'Download the CSV Template',
  uploadCsvStep2: 'Populate template with asset data',
  uploadCsvStep3: 'Save your file as a .CSV and upload',
  uploadCsvStepsDescription: 'To upload an CSV file, please follow these steps',
  uploadDocument: 'Upload Document',
  uploadDocuments: 'Upload Documents',
  uploadDocumentsToActivate:
    'Upload your compliance documents to activate your company.',
  uploaded: 'Uploaded',
  uploadedBy: 'Uploaded by',
  uploadedByAt: (by: string, at: string) => `Uploaded by: ${by} at ${at}`,
  uploadedDate: 'Uploaded date',
  uploadFailed: 'Upload Failed',
  uploadFile: 'Upload File',
  uploadFromGallery: 'Upload from Gallery',
  uploadImage: 'Upload Photo',
  uploading: 'Uploading',
  uploadingData: 'Uploading Data',
  uploadingPlan: 'Uploading Plan',
  uploadLogo: 'Upload Logo',
  uploadNewVersion: 'Upload New Version',
  uploadObservationMedia: 'Upload Observation Media',
  uploadPdf: 'Upload PDF',
  uploadPlan: 'Upload Plan',
  uploadProof: 'Upload Proof',
  uploadRegisterItems: 'Upload Register Items',
  uploadTimeout: 'Upload timeout reached',
  uploadYourCsvFile: 'Upload your CSV file',
  upTo: 'Up to {0}',
  url: 'URL',
  useChecklistForInspections: 'Use Checklist for Inspections',
  usedAsDescriptionField: 'Yes, as item title',
  usedAsItemTitle: 'Used as item title',
  usedForPreviewField: 'Yes, as a detail',
  user: 'User',
  userAlreadyVerifiedAlert:
    'You have already set a password for this user, please log in using that password.',
  userCompetencyCompleted: 'Competency completed',
  userCompetencyExpired: 'Competency expired',
  userCreated: 'User Created',
  userDeleted: 'User Deleted',
  userDetails: 'User Details',
  userDisabled: 'User Disabled',
  userDoesNotExist:
    'User does not exist. Please check your credentials and try again.',
  userExistsUnderDifferentContractor: (email: string) =>
    `The user ${email} exists under a different Contractor in the system. The user must accept your invitation to be assigned permits.`,
  userInformation: 'User Information',
  userInvolved: 'User Involved',
  userIsDeactivated: 'User is deactivated',
  userProperties: 'User Properties',
  userProperty: 'User Property',
  userRole: 'User Role',
  userRoleNotUpdated: 'User role could not be updated',
  userRoleUpdated: 'User role updated',
  users: 'Users',
  usersCheckedIn: 'Users checked in',
  userSelect: 'User Select',
  userSettingsOnOtherProjectsUnchanged:
    'Their settings on other projects will be unaffected.',
  userType: 'User Type',
  userUpdated: 'User Updated',
  userUpdateFailed: 'User Update Failed',
  useTheProvidedTemplateToEnterCompanyData:
    'Use the provided template to enter company data.',
  validAnswer: 'Valid Answer',
  validAnswerOption: 'Enter A Valid Answer Option',
  validations: 'Validations',
  validDuration: 'Valid Duration',
  validUntil: 'Valid Until',
  value: 'Value',
  valueMustBePositive: 'Value must be positive',
  verified: 'Verified',
  versionActivated: 'Version activated',
  versionDeleted: 'Version Deleted',
  versionHistory: 'Version History',
  versionName: 'Version Name',
  versions: 'Versions',
  viaQrCode: 'Via QR Code',
  video: 'Video',
  videoRecording: 'Video Recording',
  view: 'View',
  viewActions: 'View Actions',
  viewAll: 'View All',
  viewAllDocuments: 'View all Documents',
  viewAllPeriodicChecks: 'View All Periodic Checks',
  viewAllPermits: 'View All Permits',
  viewAllRegisters: 'View All Registers',
  viewAllTasks: 'View All Tasks',
  viewAllUsersOnSite: (count: number) =>
    `View All ${count} ${count === 1 ? 'User' : 'Users'} On Site`,
  viewAndDoPeriodicCheck: 'View all periodic checks and do a new check',
  viewBriefingText: 'View Briefing Text',
  viewChecklist: 'View Checklist',
  viewChecklists: 'View Checklists',
  viewCompletedInspections: 'View Completed Inspections',
  viewConditions: 'View Conditions',
  viewCreated: 'View Created',
  viewDeleted: 'View Deleted',
  viewDetails: 'View Details',
  viewDocument: 'View Document',
  viewDocuments: 'View all {0} Documents',
  viewDocumentToAcknowledge: 'View document to acknowledge',
  viewDraft: 'View Draft',
  viewEvidence: 'View Evidence',
  viewFullProfile: 'View Full Profile',
  viewInformation: 'View Information',
  viewInMyWork: 'View in My Work',
  viewInspection: 'View Inspection',
  viewMore: 'View More',
  viewName: 'View Name',
  viewObservations: 'View Observations',
  viewOnMap: 'View on Map',
  viewOnPlan: 'View on Plan',
  viewParticipant: 'View Participant',
  viewPermit: 'View Permit',
  viewPermits: 'View Permits',
  viewPermitTimeline: 'View Permit Timeline',
  viewPlan: 'View Plan',
  viewPlans: 'View Plans',
  viewProfile: 'View Profile',
  viewProject: 'View Project',
  viewReason: 'View Reason',
  viewRegisterItem: 'View Register Item',
  viewRequestedChanges: 'View Requested Changes',
  viewResponse: 'View Response',
  views: 'Views',
  viewTask: 'View Task(s)',
  viewTemplateSettings: 'View Template Settings',
  viewTranscript: 'View Transcript',
  viewUpdated: 'View Updated',
  viewVideo: 'View Video',
  visibility: 'Visibility',
  visibilityControlledByStage: (n: number, stageName: string) =>
    `Visibility controlled by ${n} rule(s) in ${stageName}`,
  visibilityRuleDescription:
    'This question is shown if one or more of the following rule(s) are met:',
  visibilityRuleRequirements:
    'All conditions below must be met for this question to be visible.',
  visibleForNProjects: (n: number) => `Visible for ${n} Projects`,
  visibleForProject: (name: string) => `Visible for Project: ${name}`,
  visibleOnItemPreview: 'Visible in item preview',
  visibleWhenQuestionIs: (question: string, answers: string) =>
    `Visible when Question ${question} is ${answers}`,
  w3wDoesNotExist: 'This what3words address does not exist',
  w3wNoInternetConnection:
    'To select a what3words location using the map, an internet connection is required. However, you can still manually enter the what3words location without internet access.',
  warning: 'Warning',
  warningConfig: 'Warning Config',
  warningDetected: 'Warning Detected',
  warningDynamicText: ({
    item,
    types = [],
    condition,
  }: {
    item: string
    types: Array<string>
    condition: string
  }) =>
    `${item} of type ${
      types.length > 1
        ? `${types.slice(0, -1).join(', ')} and ${types.at(-1)}`
        : types.join('')
    } ${types.length > 1 ? 'are' : 'is'} ${condition}`,
  warningInformation: 'Warning Information',
  warningLostDataMessageConfimation:
    'Changing this will result in loss of warning data for this question. Are you sure you want to continue?',
  warningMessage: 'You must finish create question before adding a warning',
  warningReportedAlertMessage: `Thank you for your feedback. We're reviewing it to improve the system.`,
  warningReportedMessage: 'This warning has been reported',
  warnings: 'Warnings',
  warningSettings: 'Warning Settings',
  warningText: 'Warning Text',
  warningType: 'Warning Type',
  warningTypeErrorsText: (typeErrors: Array<string>) =>
    `${
      typeErrors.length > 1
        ? `${typeErrors.slice(0, -1).join(', ')} and ${typeErrors.at(-1)}`
        : typeErrors.join('')
    }`,
  webBrowser: 'Web Browser',
  weekly: 'Weekly',
  weekNumber: (week: number) => `W${week}`,
  weHaveSentEmail: "We've sent you an email",
  welcomeBack: (name: string) => `Welcome back${name ? `, ${name}` : ''}!`,
  weSeeYoureOnMobileMessage:
    'The SiteAssist mobile app is a much better experience for mobile users!',
  weSentAConfirmationEmailTo: "We've sent a confirmation email to",
  weSentAnEmailTo: "We've sent an email to",
  what3Words: 'What3Words',
  whatsNew: "What's New",
  whichCompaniesCanViewAllInformation:
    'Which Companies can view all information?',
  whoCanApproveNewCompanies: 'Who can approve new companies?',
  workComplete: 'Work Complete',
  workCompleteDescription:
    'Declare work complete so that the permit can be closed off',
  workedBy: 'Worked by',
  workflow: 'Workflow',
  workflowCancel: 'Cancel Workflow',
  workflowDiscardProgressDescription: (workflow: string) =>
    `Are you sure you want to cancel ${workflow}? Your progress will be discarded.`,
  workflowDoNotCancel: `Don't cancel`,
  workflowSaveProgressDescription: (workflow: string) =>
    `Are you sure you want to cancel ${workflow}? Your progress will be saved.`,
  workflowSubmissionStillCancelling:
    'Still finishing the previous submission attempt. Please try again in a moment.',
  workflowSubmitting: 'Submitting',
  workflowSubmittingKeepAppOpen:
    'This can take a moment on a slow connection. Please keep the app open.',
  workflowSubmittingProgress: (progress: number) => `${progress}%`,
  workflowSubmittingYourWork: 'Submitting your work',
  workflowYesCancel: 'Yes, cancel',
  wouldYouLikeToClose: 'Would you like to Close the Permit?',
  wouldYouLikeToLogIn: 'Would you like to log in?',
  wouldYouLikeToRaiseAnyAdditionalChildPermit:
    'Would you like to raise any additional child permit?',
  wouldYouLikeToReject:
    'Would you like to Reject or Issue the Permit to Work Request?',
  yearsShort: 'y',
  yes: 'Yes',
  yesPlease: 'Yes please',
  you: 'You',
  youAreAboutToAddParticipantsToPermit: (count: number) =>
    `You are about to add ${count} ${
      count === 1 ? 'participant' : 'participants'
    } to this permit.`,
  youAreCheckedIn: 'You are checked in',
  youAreCheckedInOnPermits: (count: number) =>
    `You are checked in on ${count} ${count === 1 ? 'permit' : 'permits'}`,
  youAreOffline: 'You are Offline',
  youAreUpToDate: "You're up to date",
  youCanDeleteTheSelectedPinHere: 'You can delete the selected pin here',
  youCannotPerformThisAction: 'You cannot perform this action',
  youCantPerformThisAction: 'You can not perform this action!',
  youDontHaveAnyCustomViewsYet: `You don't have any custom views yet.`,
  youHaveBeenAddedToAChecklist: 'You have been added to a checklist',
  youHaveBeenAddedToAnInspection: 'You have been added to an inspection',
  youHaveBeenAddedToAPermit: 'You have been added to a permit',
  youreAllCaughtUp: `You're all caught up`,
  yourProfileHasBeenUpdated: 'Your profile has been updated',
  yourProjects: 'Your Projects',
  zone: 'Zone',
  zoneLabel: 'Zone Label',
  zones: 'Zones',
  zoneSelected: (selectedZones: number) =>
    `${selectedZones > 0 ? selectedZones : 'No'} ${
      selectedZones > 1 ? 'Zones' : 'Zone'
    } Selected`,
  zoom: 'Zoom',
  zoomToLayer: 'Zoom to layer',
}

const Strings: Record<SupportedLanguagesType, StringsType> = {
  en: enBaseStrings,
}

export const localizedStrings = new LocalizedStrings(Strings)
